import React, { useEffect, useMemo, useRef, useState } from "react";
import IdentityLinkDialog from "@/components/admin/IdentityLinkDialog";
import NotifiedDriversButton, { NotifiedDriversHost } from "@/components/admin/NotifiedDriversButton";
import ScheduledNotifsPanel from "@/components/admin/ScheduledNotifsPanel";
import DriverDocsStrip, { DriverDocsFilters } from "@/components/admin/DriverDocsStrip";
import AccountsFollowupPanel from "@/components/admin/AccountsFollowupPanel";
import DocsRenewalTab from "@/components/admin/DocsRenewalTab";
import DeletionReasonsTab from "@/components/admin/DeletionReasonsTab";
import CguAdminTab from "@/components/admin/CguAdminTab";
import CguUserHistoryDialog from "@/components/admin/CguUserHistoryDialog";
import ActivityLogTab from "@/components/admin/ActivityLogTab";
import SupportChat from "@/components/price/SupportChat";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Users, Car, Euro, Bell, LogOut, Save, RotateCcw, Send,
  ChevronRight, Search, Volume2, VolumeX, Music,
  Ban, RotateCw, CheckCircle2, MessageCircle, Loader2,
  Camera, XCircle, RadioTower, AlertCircle, Trash2, X,
  Gift, Copy, PenSquare, Plus, Shield, Pencil, UserCheck, UserPlus,
  History, ChevronLeft, LayoutDashboard, Wallet, TrendingUp, Activity,
  Megaphone, HeartHandshake, MapPin, Map as MapIcon,
  Phone, CreditCard, Banknote, Star, Clock, StickyNote, Zap, Sparkles, Mail, ClipboardList,
  Link2,
} from "lucide-react";
import { playPriceChime } from "@/lib/priceChime";
import ChatSheet from "@/components/price/ChatSheet";
import StudioLiveTab from "@/components/admin/StudioLiveTab";
import UsersByCityTab from "@/components/admin/UsersByCityTab";
import StudioMailTab from "@/components/admin/StudioMailTab";
import FormsTab from "@/components/admin/FormsTab";
import AdminUserEditor from "@/components/price/AdminUserEditor";
import DepositsTab from "@/pages/admin/DepositsTab";
import CreatorCodesTab from "@/pages/admin/CreatorCodesTab";
import HistoryTab from "@/pages/admin/HistoryTab";
import WalletsTab from "@/pages/admin/WalletsTab";
import UsersMapTab from "@/pages/admin/UsersMapTab";
import ImageCropDialog from "@/components/price/ImageCropDialog";
import AdminErrorBoundary from "@/components/price/AdminErrorBoundary";
import { useAdminFetch, clearAdminCache } from "@/pages/admin/_adminCache";
import { AdminRowSkeleton, AdminStatSkeleton } from "@/pages/admin/_AdminSkeletons";

/**
 * Price — Admin dashboard (cartoon/street).
 *
 * Full-control admin panel focused on the three operations the founder
 * asked for:
 *  1. See every registered client + driver
 *  2. Edit the per-km rate (and the base fee)
 *  3. Broadcast a push notification with three sound modes:
 *     - Discreet: silent push
 *     - System: the phone's default notification tone
 *     - Price: the custom Price chime (synth via Web Audio)
 */
export default function AdminDashboard() {
  const { user, profile, logout } = useAuth();
  const nav = useNavigate();
  // View state: "home" (categories + KPI) or a specific tab id.
  const [view, setView] = useState("home");
  const [overview, setOverview] = useState(null);

  const handleLogout = async () => {
    await logout();
    nav("/", { replace: true });
  };

  // Charge KPI + badges de la home. Refresh à chaque retour home.
  useEffect(() => {
    if (view !== "home") return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/price/overview");
        if (!cancel) setOverview(data || null);
      } catch (_e) {
        if (!cancel) setOverview(null);
      }
    })();
    return () => { cancel = true; };
  }, [view]);

  const kpi = overview?.kpi || {};
  const badges = overview?.badges || {};

  // Définition des 5 catégories (avec leurs sous-onglets).
  const CATEGORIES = [
    {
      id: "community",
      label: "Communauté",
      subtitle: "Passagers, covoitureurs, KYC, photos",
      icon: Users,
      color: "bg-[#FF3B3B]",
      badge: (badges.kyc_pending || 0) + (badges.photos_pending || 0) + (badges.activity_unseen || 0),
      subtabs: [
        { id: "activity", label: "Journal d'activité", icon: Bell, testid: "admin-tab-activity", badge: badges.activity_unseen },
        { id: "clients", label: "Passagers", icon: Users, testid: "admin-tab-clients" },
        { id: "drivers-list", label: "Covoitureurs", icon: Car, testid: "admin-tab-drivers-list" },
        { id: "drivers-fast", label: "Covoitureurs (rapide)", icon: Car, testid: "admin-tab-drivers-fast" },
        { id: "by-city", label: "Par ville", icon: MapPin, testid: "admin-tab-by-city" },
        { id: "map", label: "Carte", icon: MapIcon, testid: "admin-tab-map" },
        { id: "identity", label: "Identité (KYC)", icon: Shield, testid: "admin-tab-identity", badge: badges.kyc_pending },
        { id: "docs-renewal", label: "Documents à renouveler", icon: Shield, testid: "admin-tab-docs-renewal" },
        { id: "deletion-reasons", label: "Raisons de départ", icon: Users, testid: "admin-tab-deletion-reasons" },
        { id: "cgu", label: "CGU", icon: ClipboardList, testid: "admin-tab-cgu" },
        { id: "photos", label: "Photos à modérer", icon: Camera, testid: "admin-tab-photos", badge: badges.photos_pending },
      ],
    },
    {
      id: "operations",
      label: "Opérations",
      subtitle: "Live + historique des courses",
      icon: RadioTower,
      color: "bg-[#0F2634]",
      badge: badges.live_active,
      subtabs: [
        { id: "live", label: "Courses en direct", icon: RadioTower, testid: "admin-tab-live", badge: badges.live_active },
        { id: "history", label: "Historique", icon: History, testid: "admin-tab-history" },
      ],
    },
    {
      id: "messaging",
      label: "Messagerie",
      subtitle: "Chats + support utilisateurs",
      icon: MessageCircle,
      color: "bg-[#F9A825]",
      badge: badges.support_unread,
      subtabs: [
        { id: "chats", label: "Chats utilisateurs", icon: MessageCircle, testid: "admin-tab-chats" },
        { id: "support", label: "Support", icon: HeartHandshake, testid: "admin-tab-support", badge: badges.support_unread },
      ],
    },
    {
      id: "finance",
      label: "Finance",
      subtitle: "Prix, cautions, parrainage, créateurs",
      icon: Wallet,
      color: "bg-emerald-600",
      badge: badges.deposits_expiring,
      subtabs: [
        { id: "pricing", label: "Prix & commission", icon: Euro, testid: "admin-tab-pricing" },
        { id: "promo", label: "Promo globale", icon: TrendingUp, testid: "admin-tab-promo" },
        { id: "transfers", label: "Transferts en attente", icon: Wallet, testid: "admin-tab-transfers" },
        { id: "wallets", label: "Portefeuilles", icon: Wallet, testid: "admin-tab-wallets" },
        { id: "deposits", label: "Cautions Stripe", icon: Shield, testid: "admin-tab-deposits", badge: badges.deposits_expiring },
        { id: "referrals", label: "Parrainage", icon: Gift, testid: "admin-tab-referrals" },
        { id: "creators", label: "Codes créateurs", icon: Gift, testid: "admin-tab-creators" },
      ],
    },
    {
      id: "broadcast",
      label: "Diffusion",
      subtitle: "Notif push + popups store",
      icon: Megaphone,
      color: "bg-blue-600",
      badge: 0,
      subtabs: [
        { id: "notif", label: "Notification (son)", icon: Bell, testid: "admin-tab-notif" },
        { id: "popup", label: "Pop-up + notif (son)", icon: AlertCircle, testid: "admin-tab-popup" },
        { id: "surveys", label: "Notif + réponse", icon: MessageCircle, testid: "admin-tab-surveys" },
        { id: "mail", label: "E-mail HTML", icon: Mail, testid: "admin-tab-mail" },
        { id: "forms", label: "Questionnaire", icon: ClipboardList, testid: "admin-tab-forms" },
      ],
    },
    {
      id: "studio",
      label: "Studio Live",
      subtitle: "Déco en direct (Halloween, Noël…)",
      icon: Sparkles,
      color: "bg-orange-500",
      badge: 0,
      subtabs: [
        { id: "studio", label: "Décorations accueil", icon: Sparkles, testid: "admin-tab-studio" },
      ],
    },
  ];

  // Trouve la catégorie qui contient l'onglet courant (pour breadcrumb).
  const currentCategory = CATEGORIES.find((c) => c.subtabs.some((s) => s.id === view));
  const currentSubtab = currentCategory?.subtabs.find((s) => s.id === view);
  const activeCategoryId = view.startsWith("cat:") ? view.slice(4) : null;
  const activeCategory = activeCategoryId ? CATEGORIES.find((c) => c.id === activeCategoryId) : null;

  return (
    <div
      className="relative min-h-screen"
      style={{ background: "#F5F7FA", color: "#0F172A" }}
      data-testid="admin-dashboard"
    >
      <NotifiedDriversHost />
      <div
        className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(20px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          paddingLeft: "max(16px, env(safe-area-inset-left))",
          paddingRight: "max(16px, env(safe-area-inset-right))",
        }}
      >
        {/* Header — minimal, pro */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {view !== "home" ? (
              <button
                type="button"
                onClick={() => setView(currentCategory ? `cat:${currentCategory.id}` : "home")}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white ring-1 ring-black/5 shadow-sm active:scale-[0.97] transition"
                aria-label="Retour"
                data-testid="admin-back"
              >
                <ChevronLeft className="h-4 w-4 text-slate-700" strokeWidth={2.5} />
              </button>
            ) : (
              <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center shadow-sm">
                <span className="font-[Outfit] text-[17px] font-black text-white tracking-tight">P</span>
              </div>
            )}
            <div className="leading-tight">
              <div className="font-[Outfit] text-[16px] font-bold text-slate-900">Price</div>
              <div className="text-[11px] font-medium text-slate-500 tracking-wide">
                {view === "home" ? "Console admin" : (activeCategory?.label || currentCategory?.label || "Admin")}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white ring-1 ring-black/5 shadow-sm active:scale-[0.97] transition"
            aria-label="Se déconnecter"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4 text-slate-700" strokeWidth={2.5} />
          </button>
        </div>

        {/* HOME — titre + KPI + grille catégories */}
        {view === "home" && (
          <>
            <div className="mt-7">
              <h1 className="font-[Outfit] text-[28px] font-bold leading-tight tracking-tight text-slate-900">
                Bonjour <span className="text-primary">👋</span>
              </h1>
              <p className="mt-1 text-[13px] font-medium text-slate-500 truncate">
                {user?.email}
              </p>
            </div>

            {/* KPI grid — cards blanches soft, pastel accents */}
            <div className="mt-5 grid grid-cols-2 gap-2.5" data-testid="admin-kpi-grid">
              <KpiCard
                icon={<Users className="h-4 w-4 text-blue-600" strokeWidth={2.5} />}
                label="Utilisateurs"
                value={kpi.users_total ?? "—"}
                sub={`${kpi.clients_total ?? 0} pass · ${kpi.drivers_total ?? 0} cov`}
                testid="kpi-users"
              />
              <KpiCard
                icon={<Activity className="h-4 w-4 text-emerald-600" strokeWidth={2.5} />}
                label="Courses 24h"
                value={kpi.rides_today ?? "—"}
                sub="créées"
                testid="kpi-rides"
              />
              <KpiCard
                icon={<TrendingUp className="h-4 w-4 text-primary" strokeWidth={2.5} />}
                label="CA du jour"
                value={`${Number(kpi.revenue_today || 0).toFixed(2)}€`}
                sub="courses terminées"
                testid="kpi-revenue"
              />
              <KpiCard
                icon={<Shield className="h-4 w-4 text-amber-600" strokeWidth={2.5} />}
                label="Cautions"
                value={kpi.active_deposits ?? "—"}
                sub={badges.deposits_expiring ? `${badges.deposits_expiring} à renouveler` : "actives"}
                accent={badges.deposits_expiring ? "amber" : null}
                testid="kpi-deposits"
              />
            </div>

            {/* Catégories — grille 2 colonnes style iOS tiles */}
            <div className="mt-7">
              <div className="flex items-center justify-between mb-2.5 px-1">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                  Sections
                </p>
                <p className="text-[11px] font-medium text-slate-400">
                  {CATEGORIES.length} rubriques
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {CATEGORIES.map((cat) => (
                  <CategoryTile
                    key={cat.id}
                    category={cat}
                    onClick={() => {
                      setView(`cat:${cat.id}`);
                      if (cat.id === "community") { prefetchUsers().catch(() => {}); }
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Widget rapide — Covoitureurs en ligne (chargement <100 ms) */}
            <DriversOnlineWidget onOpenFull={() => setView("drivers-fast")} />

            {/* Widget stats — répartition passagers / conducteurs par mode actif */}
            <RoleStatsWidget />
          </>
        )}

        {/* CATÉGORIE — sous-onglets en liste */}
        {activeCategory && (
          <div className="mt-6">
            <div className="mb-5">
              <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl shadow-sm ${activeCategory.color}`}>
                <activeCategory.icon className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <h1 className="mt-3 font-[Outfit] text-[22px] font-bold leading-tight tracking-tight text-slate-900">
                {activeCategory.label}
              </h1>
              <p className="mt-1 text-[13px] font-medium text-slate-500">
                {activeCategory.subtitle}
              </p>
            </div>
            <div className="space-y-2">
              {activeCategory.subtabs.map((s) => (
                <SubtabRow
                  key={s.id}
                  subtab={s}
                  onClick={() => setView(s.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* CONTENU ONGLET */}
        {currentSubtab && (
          <div className="mt-5">
            <div className="mb-4 flex items-center gap-2.5">
              <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
                <currentSubtab.icon className="h-4 w-4 text-slate-700" strokeWidth={2.5} />
              </div>
              <h2 className="font-[Outfit] text-[17px] font-bold text-slate-900 tracking-tight">
                {currentSubtab.label}
              </h2>
            </div>
            <AdminErrorBoundary key={view} label={view}>
              {view === "clients" && <UsersTab initialTab="clients" />}
              {view === "drivers-list" && <UsersTab initialTab="drivers" />}
              {view === "drivers-fast" && <DriversFastTab />}
              {view === "docs-renewal" && <DocsRenewalTab />}
              {view === "deletion-reasons" && <DeletionReasonsTab />}
              {view === "cgu" && <CguAdminTab />}
              {view === "activity" && <ActivityLogTab />}
              {view === "map" && <UsersMapTab />}
              {view === "pricing" && <PricingTab />}
              {view === "promo" && <PromoTab />}
              {view === "transfers" && <PendingTransfersTab />}
              {view === "notif" && <NotifTab />}
              {view === "studio" && <StudioLiveTab />}
              {view === "by-city" && <UsersByCityTab />}
              {view === "mail" && <StudioMailTab />}
              {view === "forms" && <FormsTab />}
              {view === "popup" && <PopupTab />}
              {view === "surveys" && <SurveysTab />}
              {view === "chats" && <ChatsTab />}
              {view === "support" && <SupportChatTab />}
              {view === "live" && <LiveRidesTab />}
              {view === "photos" && <PhotosTab />}
              {view === "referrals" && <ReferralsTab />}
              {view === "identity" && <IdentityTab />}
              {view === "deposits" && <DepositsTab />}
              {view === "creators" && <CreatorCodesTab />}
              {view === "history" && <HistoryTab />}
              {view === "wallets" && <WalletsTab />}
            </AdminErrorBoundary>
          </div>
        )}

        <div className="mt-8 text-center text-[11px] font-medium tracking-widest text-slate-400">
          Price · Admin Console
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, accent, testid }) {
  // "accent" : "amber" | "red" | null. Donne un léger surlignage.
  const ring = accent === "amber"
    ? "ring-amber-200"
    : accent === "red"
    ? "ring-red-200"
    : "ring-black/5";
  return (
    <div
      className={`rounded-2xl bg-white shadow-sm ring-1 ${ring} px-3.5 py-3`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-slate-500">
        {icon}
        <span className="text-[10px] font-semibold uppercase tracking-widest">{label}</span>
      </div>
      <div className="mt-1 font-[Outfit] text-[22px] font-bold leading-none tabular-nums text-slate-900">
        {value}
      </div>
      {sub && (
        <div className="mt-1 text-[10.5px] font-medium text-slate-400 truncate">
          {sub}
        </div>
      )}
    </div>
  );
}

/**
 * Tile iOS — 2 colonnes, carrée, icône couleur + label. Clean, pro.
 * Remplace l'ancienne CategoryCard "pop/street" bariolée.
 */
function CategoryTile({ category, onClick }) {
  const Icon = category.icon;
  const hasBadge = Boolean(category.badge && category.badge > 0);
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex flex-col items-start gap-3 rounded-2xl bg-white px-3.5 py-4 shadow-sm ring-1 ring-black/5 active:scale-[0.98] transition text-left"
      data-testid={`admin-cat-${category.id}`}
    >
      <div className={`h-11 w-11 rounded-xl grid place-items-center shadow-sm ${category.color}`}>
        <Icon className="h-5 w-5 text-white" strokeWidth={2.5} />
      </div>
      <div className="min-w-0 w-full">
        <div className="font-[Outfit] text-[14px] font-bold leading-tight text-slate-900 truncate">
          {category.label}
        </div>
        <div className="mt-0.5 text-[11px] font-medium text-slate-500 leading-snug line-clamp-2">
          {category.subtitle}
        </div>
      </div>
      {hasBadge && (
        <span
          className="absolute top-3 right-3 min-w-[20px] h-[20px] px-1.5 grid place-items-center rounded-full bg-primary text-white text-[10.5px] font-bold shadow-sm"
          data-testid={`admin-cat-${category.id}-badge`}
        >
          {category.badge > 99 ? "99+" : category.badge}
        </span>
      )}
    </button>
  );
}

function SubtabRow({ subtab, onClick }) {
  const Icon = subtab.icon;
  const hasBadge = Boolean(subtab.badge && subtab.badge > 0);
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 rounded-xl bg-white px-3.5 py-3.5 shadow-sm ring-1 ring-black/5 active:scale-[0.99] transition"
      data-testid={subtab.testid}
    >
      <div className="h-9 w-9 flex-none rounded-lg bg-slate-100 grid place-items-center">
        <Icon className="h-4 w-4 text-slate-700" strokeWidth={2.5} />
      </div>
      <div className="flex-1 text-left font-[Outfit] text-[14px] font-semibold text-slate-900">
        {subtab.label}
      </div>
      {hasBadge && (
        <span className="min-w-[20px] h-[20px] px-1.5 grid place-items-center rounded-full bg-primary text-white text-[10.5px] font-bold">
          {subtab.badge > 99 ? "99+" : subtab.badge}
        </span>
      )}
      <ChevronRight className="h-4 w-4 flex-none text-slate-300" strokeWidth={2.5} />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Shared — UserPicker (recherche live) + TemplateBar (charger/sauver) */
/* ------------------------------------------------------------------ */

function UserPicker({ value, onChange, label = "Destinataire" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/users/search`, { params: { q, limit: 15 } });
        setResults(r.data?.users || []);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <div className="rounded-xl bg-white p-3 street-border-thin">
      <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 mb-1.5">{label}</div>
      {value ? (
        <div className="flex items-center gap-2 rounded-lg bg-secondary/40 p-2 street-border-thin">
          <div className={`grid h-8 w-8 flex-none place-items-center rounded-lg text-[10px] font-black uppercase ${value.role === "driver" ? "bg-[#FFE9D6]" : "bg-[#FFD9DA]"}`}>
            {value.role === "driver" ? "COV" : "PAX"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-[Outfit] text-[12px] font-black truncate">{value.label}</div>
            <div className="text-[10px] text-foreground/60 truncate">{value.email}</div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            data-testid="user-picker-clear"
            className="grid h-7 w-7 flex-none place-items-center rounded-lg border-2 border-black bg-white active:translate-y-[1px]"
            aria-label="Changer"
          >
            <X className="h-3.5 w-3.5" strokeWidth={3} />
          </button>
        </div>
      ) : (
        <>
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Nom, email ou téléphone…"
            data-testid="user-picker-input"
            className="w-full rounded-lg bg-secondary/40 px-3 py-2 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:bg-white"
            style={{ fontSize: "16px" }}
          />
          {open && (
            <div className="mt-2 max-h-60 overflow-y-auto space-y-1">
              {loading && <div className="text-[11px] text-foreground/50 py-2 text-center">Recherche…</div>}
              {!loading && results.length === 0 && <div className="text-[11px] text-foreground/50 py-2 text-center">Aucun résultat</div>}
              {results.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => { onChange(u); setOpen(false); setQ(""); }}
                  data-testid={`user-picker-choice-${u.id}`}
                  className="w-full text-left flex items-center gap-2 rounded-lg bg-secondary/40 hover:bg-secondary p-2 street-border-thin"
                >
                  <div className={`grid h-7 w-7 flex-none place-items-center rounded-lg text-[9px] font-black uppercase ${u.role === "driver" ? "bg-[#FFE9D6]" : "bg-[#FFD9DA]"}`}>
                    {u.role === "driver" ? "COV" : "PAX"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-bold truncate">{u.label}</div>
                    <div className="text-[10px] text-foreground/60 truncate">{u.email}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TemplateBar({ kind, current, onLoad }) {
  const [templates, setTemplates] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/price/message-templates", { params: { kind } });
      setTemplates(r.data?.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const name = saveName.trim();
    if (!name) return toast.error("Nom du modèle obligatoire");
    if (!current?.title?.trim() || !current?.body?.trim()) {
      return toast.error("Remplis titre et message avant d'enregistrer");
    }
    setBusy(true);
    try {
      const payload = {
        kind, name,
        title: current.title.trim(),
        body: current.body.trim(),
        sound: kind === "notif" ? current.sound : undefined,
        action_label: kind === "popup" ? (current.action_label || null) : undefined,
        action_target: kind === "popup" ? (current.action_target || null) : undefined,
      };
      await api.post("/admin/price/message-templates", payload);
      toast.success(`Modèle "${name}" enregistré`);
      setSaveOpen(false); setSaveName("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try {
      await api.delete(`/admin/price/message-templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="rounded-xl bg-white p-2 street-border-thin">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          data-testid={`template-bar-${kind}-load`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-secondary/40 hover:bg-secondary px-3 py-2 text-[11px] font-black uppercase tracking-widest street-border-thin street-press"
        >
          <PenSquare className="h-3.5 w-3.5" />
          {templates.length > 0 ? `Modèles (${templates.length})` : "Modèles"}
        </button>
        <button
          type="button"
          onClick={() => setSaveOpen((v) => !v)}
          data-testid={`template-bar-${kind}-save`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-[11px] font-black uppercase tracking-widest street-border-thin street-press"
        >
          <Save className="h-3.5 w-3.5" />
          Enregistrer
        </button>
      </div>

      {pickerOpen && (
        <div className="mt-2 space-y-1 max-h-52 overflow-y-auto">
          {templates.length === 0 && (
            <div className="text-[11px] text-foreground/50 text-center py-3">
              Aucun modèle. Remplis un message puis clique &laquo;&nbsp;Enregistrer&nbsp;&raquo;.
            </div>
          )}
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => { onLoad(t); setPickerOpen(false); toast.success(`Modèle "${t.name}" chargé`); }}
              data-testid={`template-item-${t.id}`}
              className="w-full text-left flex items-start gap-2 rounded-lg bg-secondary/40 hover:bg-secondary p-2 street-border-thin"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-black truncate">{t.name}</div>
                <div className="text-[10px] text-foreground/60 truncate">{t.title} · {t.body?.slice(0, 60)}</div>
              </div>
              <button
                type="button"
                onClick={(e) => del(t.id, e)}
                data-testid={`template-delete-${t.id}`}
                className="grid h-6 w-6 flex-none place-items-center rounded-md bg-red-500 text-white street-border-thin street-press"
                aria-label="Supprimer le modèle"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </button>
          ))}
        </div>
      )}

      {saveOpen && (
        <div className="mt-2 flex gap-2">
          <input
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="Nom du modèle (ex. Relance Stripe)"
            data-testid={`template-bar-${kind}-name`}
            className="flex-1 rounded-lg bg-secondary/40 px-3 py-2 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:bg-white text-[12px]"
            style={{ fontSize: "16px" }}
          />
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid={`template-bar-${kind}-confirm`}
            className="rounded-lg bg-primary text-primary-foreground border-2 border-black px-3 py-2 text-[11px] font-black uppercase disabled:opacity-50 active:translate-y-[1px]"
          >
            {busy ? "…" : "OK"}
          </button>
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Tab: Users                                                          */
/* ------------------------------------------------------------------ */
// Cache module-level partagé par UsersTab. Permet l'affichage
// instantané lorsqu'on re-navigue vers "Passagers" ou "Covoitureurs"
// depuis la catégorie Communauté — au lieu de refetch et de voir un
// spinner à chaque ouverture, on rend le contenu direct puis on
// rafraîchit en tâche de fond si les données ont plus de CACHE_MS.
const CACHE_MS = 60_000;
const _usersCache = { clients: null, drivers: null, clientsAt: 0, driversAt: 0 };

async function _fetchClientsRaw() {
  // Attention : on NE catche PAS ici — l'erreur doit remonter à
  // `UsersTab.load()` pour qu'il affiche un bandeau "Réessayer"
  // au lieu de rester bloqué en "Chargement…".
  const r = await api.get("/admin/clients");
  const data = r?.data || {};
  return data?.clients || data?.items || (Array.isArray(data) ? data : []);
}
async function _fetchDriversRaw() {
  const r = await api.get("/admin/drivers");
  const data = r?.data || {};
  return data?.drivers || data?.items || (Array.isArray(data) ? data : []);
}

// Prefetch — à appeler dès qu'on entre dans la catégorie "Communauté"
// pour que les 2 listes soient prêtes avant même le premier clic.
export async function prefetchUsers() {
  const now = Date.now();
  const jobs = [];
  if (!_usersCache.clients || now - _usersCache.clientsAt > CACHE_MS) {
    jobs.push(_fetchClientsRaw().then((list) => {
      _usersCache.clients = list;
      _usersCache.clientsAt = Date.now();
    }));
  }
  if (!_usersCache.drivers || now - _usersCache.driversAt > CACHE_MS) {
    jobs.push(_fetchDriversRaw().then((list) => {
      _usersCache.drivers = list;
      _usersCache.driversAt = Date.now();
    }));
  }
  await Promise.all(jobs);
}

function UsersTab({ initialTab = "clients" }) {
  const [tab, setTab] = useState(initialTab === "drivers" ? "drivers" : "clients");
  const [clients, setClients] = useState(_usersCache.clients || []);
  const [drivers, setDrivers] = useState(_usersCache.drivers || []);
  const [q, setQ] = useState("");
  // Un spinner ne s'affiche QUE si la liste demandée n'a jamais été
  // chargée (cache vide). Un refresh en arrière-plan reste transparent.
  const [loading, setLoading] = useState(
    initialTab === "drivers" ? !_usersCache.drivers : !_usersCache.clients,
  );
  // Erreur côté serveur : si un fetch échoue (timeout prod, pod qui
  // redémarre, 520 CF…) on garde quand même le cache précédent et on
  // affiche un bandeau "Réessayer" plutôt qu'un écran blanc.
  const [errMsg, setErrMsg] = useState("");

  const load = React.useCallback(async (force = false) => {
    const now = Date.now();
    const jobs = [];
    if (force || !_usersCache.clients || now - _usersCache.clientsAt > CACHE_MS) {
      jobs.push(_fetchClientsRaw().then((list) => {
        if (Array.isArray(list)) {
          _usersCache.clients = list;
          _usersCache.clientsAt = Date.now();
          setClients(list);
        }
      }).catch((e) => { throw e; }));
    }
    if (force || !_usersCache.drivers || now - _usersCache.driversAt > CACHE_MS) {
      jobs.push(_fetchDriversRaw().then((list) => {
        if (Array.isArray(list)) {
          _usersCache.drivers = list;
          _usersCache.driversAt = Date.now();
          setDrivers(list);
        }
      }).catch((e) => { throw e; }));
    }
    if (jobs.length === 0) {
      setLoading(false);
      setErrMsg("");
      return;
    }
    try {
      // allSettled : on n'écrase pas l'un si l'autre réussit — meilleur
      // UX quand /admin/drivers rame et que /admin/clients va vite.
      const results = await Promise.allSettled(jobs);
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length === jobs.length) {
        const msg = formatApiError(failed[0]?.reason, "Impossible de charger la liste");
        setErrMsg(msg);
        toast.error(msg);
      } else {
        setErrMsg("");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (initialTab === "drivers") setTab("drivers");
    else if (initialTab === "clients") setTab("clients");
  }, [initialTab]);

  const data = tab === "clients" ? clients : drivers;
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return data;
    return data.filter((u) => {
      const s = (
        (u.email || "") + " " +
        (u.full_name || "") + " " +
        (u.first_name || "") + " " +
        (u.last_name || "") + " " +
        (u.phone || "")
      ).toLowerCase();
      return s.includes(qq);
    });
  }, [data, q]);

  // "Vraiment vide" = liste chargée (pas de spinner) et 0 résultats.
  const isEmpty = !loading && !errMsg && filtered.length === 0;

  return (
    <div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <StatCard label="Passagers" value={clients.length} tone="primary" />
        <StatCard label="Covoitureurs" value={drivers.length} tone="accent" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => setTab("clients")}
          className={`street-press rounded-2xl py-2.5 text-[12px] font-black uppercase tracking-widest street-border-thin street-shadow-sm ${tab === "clients" ? "bg-primary text-primary-foreground" : "bg-white text-foreground"}`}
          data-testid="users-tab-clients"
        >
          <Users className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
          Passagers
        </button>
        <button
          onClick={() => setTab("drivers")}
          className={`street-press rounded-2xl py-2.5 text-[12px] font-black uppercase tracking-widest street-border-thin street-shadow-sm ${tab === "drivers" ? "bg-primary text-primary-foreground" : "bg-white text-foreground"}`}
          data-testid="users-tab-drivers"
        >
          <Car className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
          Covoitureurs
        </button>
      </div>

      <div className="relative mt-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/50" />
        <input
          type="search"
          placeholder="Rechercher email ou nom…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-2xl bg-white pl-9 pr-4 py-3 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:ring-4 focus:ring-primary/25"
          style={{ fontSize: "16px" }}
        />
      </div>

      {errMsg && data.length === 0 && (
        <div className="mt-3 rounded-2xl bg-red-50 p-4 text-center street-border-thin street-shadow-sm" data-testid="users-tab-error">
          <div className="text-sm font-[Outfit] font-black text-red-700 mb-2">
            Serveur injoignable
          </div>
          <div className="text-[12px] text-red-800/80 mb-3">
            {errMsg}
          </div>
          <button
            type="button"
            onClick={() => { setLoading(true); load(true); }}
            className="street-press rounded-xl bg-primary px-4 py-2 text-[12px] font-black text-primary-foreground street-border-thin street-shadow-sm"
            data-testid="users-tab-retry"
          >
            <RotateCw className="inline h-3.5 w-3.5 mr-1 -mt-0.5" /> Réessayer
          </button>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {loading && data.length === 0 && !errMsg && (
          <>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-neutral-200" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-32 rounded bg-neutral-200" />
                    <div className="h-3 w-48 rounded bg-neutral-100" />
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
        {isEmpty && (
          <div className="rounded-2xl bg-white p-6 text-center text-sm text-foreground/60 street-border-thin street-shadow-sm">
            Aucun utilisateur
          </div>
        )}
        {filtered.map((u, idx) => (
          <UserRow key={u.id || u.user_id || u.email || `row-${idx}`} u={u} kind={tab} onChanged={() => load(true)} />
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const bg = tone === "primary" ? "bg-primary" : "bg-accent";
  const fg = tone === "primary" ? "text-primary-foreground" : "text-accent-foreground";
  return (
    <div className={`rounded-2xl ${bg} p-3 street-border street-shadow-sm`}>
      <div className={`text-[10px] font-black uppercase tracking-widest ${fg} opacity-80`}>{label}</div>
      <div className={`mt-1 font-[Outfit] text-[26px] font-black ${fg} leading-none`}>{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Compteur marketing (pricecovoit.fr — "1247 personnes inscrites")    */
/* ------------------------------------------------------------------ */
function WaitlistCounterCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState({ count: 0, base_count: 0, auto_from_users: false, real_users: null });
  const [draft, setDraft] = useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/price/waitlist/count");
      setState(data || {});
      setDraft(String(data?.base_count ?? ""));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const n = parseInt(draft, 10);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Entre un nombre entier ≥ 0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/price/waitlist/count", { base_count: n });
      setState(data || {});
      setDraft(String(data?.base_count ?? ""));
      toast.success("Compteur mis à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleAuto = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/price/waitlist/count", { auto_from_users: !state.auto_from_users });
      setState(data || {});
      toast.success(data?.auto_from_users ? "Mode auto activé (base + inscrits réels)" : "Mode manuel");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 rounded-2xl bg-white p-4 street-border street-shadow-sm" data-testid="admin-waitlist-card">
      <div className="flex items-center gap-2">
        <RadioTower className="h-4 w-4 text-primary" />
        <div className="font-[Outfit] text-[13px] font-black uppercase tracking-widest">Compteur marketing</div>
      </div>
      <div className="mt-1 text-[11px] text-foreground/60 font-medium">
        Chiffre affiché sur <span className="font-mono">pricecovoit.fr</span> (« personnes inscrites »).
      </div>

      {loading ? (
        <div className="mt-3 text-sm text-foreground/60">Chargement…</div>
      ) : (
        <>
          <div className="mt-3 rounded-xl bg-primary/10 p-3 street-border-thin flex items-baseline gap-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Affiché :</div>
            <div className="font-[Outfit] text-2xl font-black text-primary" data-testid="admin-waitlist-current">
              {(state.count || 0).toLocaleString("fr-FR")}
            </div>
            {state.auto_from_users && (
              <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-emerald-700">
                Auto ({state.base_count} + {state.real_users ?? 0})
              </div>
            )}
          </div>

          <div className="mt-3">
            <label className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
              Nombre de base
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="number"
                min="0"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="admin-waitlist-input"
                className="flex-1 rounded-xl bg-white px-3 py-2 font-[Outfit] font-black street-border-thin outline-none focus:ring-4 focus:ring-primary/25"
                style={{ fontSize: "16px" }}
                placeholder="1247"
              />
              <button
                onClick={save}
                disabled={saving}
                data-testid="admin-waitlist-save"
                className="inline-flex items-center gap-1 rounded-xl bg-primary px-3 py-2 text-[12px] font-black text-primary-foreground street-border-thin street-press disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" /> Enregistrer
              </button>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between rounded-xl bg-secondary/50 p-2 street-border-thin">
            <div className="text-[11px] font-bold text-foreground/80">
              Ajouter automatiquement les vrais inscrits
            </div>
            <button
              onClick={toggleAuto}
              disabled={saving}
              data-testid="admin-waitlist-auto"
              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-black street-border-thin street-press disabled:opacity-50 ${
                state.auto_from_users ? "bg-emerald-500 text-white" : "bg-white text-foreground"
              }`}
            >
              {state.auto_from_users ? "Activé" : "Désactivé"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function UserRow({ u, kind, onChanged }) {
  const name = u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "Sans nom";
  const initials = (name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0] || "").join("") || "?").toUpperCase();
  const [busy, setBusy] = useState(false);
  const [cguOpen, setCguOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [stripeLinkDialog, setStripeLinkDialog] = useState(null); // { url, expires_at }
  const [stripeBusy, setStripeBusy] = useState(false);
  const isSuspended = u.status === "suspended" || !!u.is_suspended;
  const isPending = u.status === "pending" || (!u.status && kind === "drivers");
  const userId = u.user_id || u.id;
  // Drivers qui n'ont pas encore activé les charges Stripe → on peut
  // leur générer un lien d'onboarding.
  const canSendStripeLink = kind === "drivers" && !u.stripe_charges_enabled;

  const sendStripeLink = async () => {
    if (stripeBusy) return;
    setStripeBusy(true);
    try {
      const r = await api.post(`/admin/drivers/${u.id}/send-stripe-link`);
      const url = r?.data?.url;
      if (!url) throw new Error("Lien Stripe non renvoyé par le serveur");
      setStripeLinkDialog({ url, expires_at: r?.data?.expires_at });
      toast.success("Lien envoyé (push + email + in-app) — tu peux aussi le copier ici");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStripeBusy(false);
    }
  };

  const act = async (verb) => {
    if (busy) return;
    let reason = "";
    if (verb === "suspend") {
      reason = window.prompt("Motif de suspension (visible par l'utilisateur) ?", "");
      if (reason === null) return; // canceled
    }
    if (verb === "delete") {
      const confirmText = window.prompt(
        `⚠️ Suppression définitive de ${u.email}\n\nCette action est IRRÉVERSIBLE. Elle efface :\n• Le compte et le profil\n• La cagnotte et l'historique parrainage\n• Les popups & notifications reçues\n\nLes trajets passés restent (anonymisés pour la compta).\n\nPour confirmer, tape SUPPRIMER :`,
        "",
      );
      if (confirmText !== "SUPPRIMER") {
        if (confirmText !== null) toast.error("Suppression annulée (texte incorrect)");
        return;
      }
    }
    setBusy(true);
    try {
      if (verb === "delete") {
        await api.delete(`/admin/price/users/${userId}`);
        toast.success("Compte supprimé");
        onChanged && onChanged();
        return;
      }
      if (verb === "toggle-test") {
        const next = !u.is_test_driver;
        await api.post(`/admin/drivers/${u.id}/test-mode`, { is_test_driver: next });
        toast.success(next ? "Mode test ACTIVÉ — reçoit toutes les demandes" : "Mode test désactivé");
        onChanged && onChanged();
        return;
      }
      const base = kind === "drivers" ? `/admin/drivers/${u.id}` : `/admin/clients/${userId}`;
      const path =
        verb === "suspend" ? `${base}/suspend` :
        verb === "unsuspend" ? `${base}/unsuspend` :
        verb === "approve" ? `${base}/approve` : "";
      await api.post(path, verb === "suspend" ? { reason } : {});
      toast.success(
        verb === "suspend" ? "Compte suspendu" :
        verb === "unsuspend" ? "Compte réactivé" : "Compte approuvé"
      );
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm">
      <div className="flex items-start gap-3">
        <div className={`grid h-10 w-10 flex-none place-items-center rounded-xl street-border-thin ${
          kind === "drivers" ? "bg-accent text-accent-foreground" : "bg-primary/15 text-primary"
        }`}>
          <span className="font-[Outfit] text-[13px] font-black">{initials}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-[Outfit] text-[14px] font-black leading-tight">{name}</div>
          <div className="truncate text-[12px] text-foreground/60 font-medium">
            {u.email}
          </div>
          {kind === "drivers" && (u.vehicle_brand_model || u.license_plate) && (
            <div className="mt-1 flex flex-wrap gap-1">
              {u.vehicle_brand_model && (
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-foreground/80 street-border-thin">
                  <Car className="h-3 w-3" />
                  {u.vehicle_brand_model}
                </span>
              )}
              {u.license_plate && (
                <span className="inline-flex items-center rounded-full bg-accent/30 px-2 py-0.5 text-[10px] font-mono font-bold text-foreground street-border-thin">
                  {u.license_plate}
                </span>
              )}
            </div>
          )}
        </div>
        {u.status && (
          <span className={`flex-none text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full street-border-thin ${
            isSuspended || u.status === "rejected" ? "bg-red-200 text-red-900" :
            u.status === "approved" || u.status === "active" ? "bg-emerald-200 text-emerald-900" :
            "bg-yellow-200 text-yellow-900"
          }`} data-testid={`user-status-${u.id || userId}`}>
            {isSuspended ? "suspendu" : u.status}
          </span>
        )}
      </div>

      {/* Action row — kept on its own line so mobile viewports never clip
          the Approuver / Suspendre / Réactiver buttons (feedback Amine). */}
      <div className="mt-3 flex flex-wrap gap-2">
        {kind === "drivers" && isPending && (
          <button
            onClick={() => act("approve")}
            disabled={busy}
            data-testid={`btn-approve-${u.id}`}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-[11px] font-black text-white street-border-thin street-press disabled:opacity-50"
            title="Approuver le covoitureur"
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Approuver
          </button>
        )}
        {!isSuspended && (
          <button
            onClick={() => act("suspend")}
            disabled={busy}
            data-testid={`btn-suspend-${u.id || userId}`}
            className="inline-flex items-center gap-1 rounded-lg bg-red-500 px-3 py-1.5 text-[11px] font-black text-white street-border-thin street-press disabled:opacity-50"
            title="Suspendre le compte"
          >
            <Ban className="h-3.5 w-3.5" /> Suspendre
          </button>
        )}
        {isSuspended && (
          <button
            onClick={() => act("unsuspend")}
            disabled={busy}
            data-testid={`btn-unsuspend-${u.id || userId}`}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-[11px] font-black text-white street-border-thin street-press disabled:opacity-50"
            title="Réactiver le compte"
          >
            <RotateCw className="h-3.5 w-3.5" /> Réactiver
          </button>
        )}
        {kind === "drivers" && (
          <button
            onClick={() => act("toggle-test")}
            disabled={busy}
            data-testid={`btn-test-mode-${u.id}`}
            className={
              "inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-black street-border-thin street-press disabled:opacity-50 " +
              (u.is_test_driver ? "bg-amber-400 text-black" : "bg-white text-foreground")
            }
            title={u.is_test_driver
              ? "Mode test ACTIF : reçoit toutes les demandes. Cliquer pour désactiver."
              : "Activer le mode test (reçoit toutes les demandes, hors contraintes)"}
          >
            🧪 {u.is_test_driver ? "Test actif" : "Mode test"}
          </button>
        )}
        {canSendStripeLink && (
          <button
            onClick={sendStripeLink}
            disabled={stripeBusy || busy}
            data-testid={`btn-stripe-link-${u.id}`}
            className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 px-3 py-1.5 text-[11px] font-black text-white street-border-thin street-press disabled:opacity-50"
            title="Génère un lien d'onboarding Stripe et l'envoie au covoitureur (push + email + in-app)"
          >
            <Zap className="h-3.5 w-3.5" /> {stripeBusy ? "…" : "Lien Stripe"}
          </button>
        )}
        <button
          onClick={() => setEditorOpen(true)}
          disabled={busy}
          data-testid={`btn-edit-user-${u.id || userId}`}
          className="inline-flex items-center gap-1 rounded-lg bg-blue-500 px-3 py-1.5 text-[11px] font-black text-white street-border-thin street-press disabled:opacity-50"
          title="Modifier tout le profil"
        >
          <Pencil className="h-3.5 w-3.5" /> Modifier
        </button>
        <button
          onClick={() => setPopupOpen(true)}
          disabled={busy}
          data-testid={`btn-popup-${u.id || userId}`}
          className="inline-flex items-center gap-1 rounded-lg bg-amber-400 px-3 py-1.5 text-[11px] font-black text-neutral-900 street-border-thin street-press disabled:opacity-50"
          title="Envoyer un message popup"
        >
          <AlertCircle className="h-3.5 w-3.5" /> Message
        </button>
        <button
          onClick={() => setCguOpen(true)}
          data-testid={`btn-cgu-history-${u.id || userId}`}
          className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-[11px] font-black text-neutral-900 street-border-thin street-press"
          title="Historique d'acceptation des CGU"
        >
          <ClipboardList className="h-3.5 w-3.5" /> CGU
        </button>
        <button
          onClick={() => act("delete")}
          disabled={busy}
          data-testid={`btn-delete-user-${u.id || userId}`}
          className="inline-flex items-center gap-1 rounded-lg bg-neutral-900 px-3 py-1.5 text-[11px] font-black text-white street-border-thin street-press disabled:opacity-50"
          title="Supprimer définitivement le compte"
        >
          <Trash2 className="h-3.5 w-3.5" /> Supprimer
        </button>
      </div>

      {popupOpen && (
        <AdminPopupComposer
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          preset={{ target_type: "user", target_user_id: userId, targetLabel: `${name} · ${u.email}` }}
        />
      )}

      {cguOpen && <CguUserHistoryDialog userId={userId} onClose={() => setCguOpen(false)} />}

      {editorOpen && (
        <AdminUserEditor
          userId={userId}
          initialRole={kind === "drivers" ? "driver" : "client"}
          onClose={() => setEditorOpen(false)}
          onSaved={() => onChanged && onChanged()}
        />
      )}

      {stripeLinkDialog && (
        <StripeLinkDialog
          driver={u}
          driverName={name}
          link={stripeLinkDialog}
          onClose={() => setStripeLinkDialog(null)}
        />
      )}
    </div>
  );
}

/* Modal qui affiche le lien Stripe généré par l'admin, avec bouton
   "Copier" et récap des canaux déjà notifiés (push + email + in-app).
   L'admin peut ainsi coller le lien dans WhatsApp / SMS / autre channel
   en plus des envois automatiques côté serveur. */
function StripeLinkDialog({ driverName, driver, link, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      toast.success("Lien copié dans le presse-papier");
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      toast.error("Copie auto bloquée — sélectionne et copie manuellement");
    }
  };
  return (
    <div
      className="fixed inset-0 z-[9999] grid place-items-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="stripe-link-dialog"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 street-border street-shadow"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-[Outfit] text-[16px] font-black">Lien Stripe généré</div>
            <div className="mt-0.5 text-[12px] text-foreground/60 truncate">
              Covoitureur : <span className="font-bold text-foreground">{driverName}</span>
              {driver?.email ? ` · ${driver.email}` : ""}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="stripe-link-close"
            className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-white street-border-thin active:translate-y-[1px]"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>

        <div className="mt-4 rounded-xl bg-emerald-50 p-3 street-border-thin">
          <div className="text-[11px] font-black uppercase tracking-widest text-emerald-800">
            ✅ Déjà envoyé automatiquement
          </div>
          <ul className="mt-1 space-y-0.5 text-[12px] text-foreground/80">
            <li>• Push mobile sur l'app Price</li>
            <li>• Email à <b>{driver?.email || "l'adresse enregistrée"}</b></li>
            <li>• Message in-app (reste visible jusqu'au clic)</li>
          </ul>
        </div>

        <div className="mt-4">
          <div className="mb-1.5 text-[11px] font-black uppercase tracking-widest text-foreground/60">
            Lien à copier (WhatsApp / SMS / autre)
          </div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={link.url}
              onClick={(e) => e.currentTarget.select()}
              data-testid="stripe-link-input"
              className="min-w-0 flex-1 rounded-lg bg-secondary/40 px-3 py-2 font-mono text-[11px] street-border-thin outline-none"
            />
            <button
              onClick={copy}
              data-testid="stripe-link-copy"
              className={`inline-flex flex-none items-center gap-1 rounded-lg px-3 py-2 text-[11px] font-black street-border-thin street-press ${
                copied ? "bg-emerald-500 text-white" : "bg-primary text-primary-foreground"
              }`}
            >
              {copied ? "✓ Copié" : "Copier"}
            </button>
          </div>
          <div className="mt-2 text-[11px] text-emerald-700 font-bold">
            💡 Lien stable valide <b>24h</b> — chaque clic ouvre une session Stripe neuve, plus de "lien expiré".
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tab: Pricing                                                        */
/* ------------------------------------------------------------------ */
/**
 * Admin — Promo globale (Happy hour). L'admin bascule un toggle,
 * règle le pourcentage (0–90) et le libellé affiché aux passagers.
 * Changement en temps réel : le ClientDashboard poll /price/promo/active
 * toutes les 30 s et affiche une bannière + prix barré dès que `active`.
 */
function PromoTab() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draftPct, setDraftPct] = useState("20");
  const [draftLabel, setDraftLabel] = useState("Happy hour -20% jusqu'à minuit");

  const load = async () => {
    try {
      const r = await api.get("/admin/price/promo");
      setState(r.data);
      if (typeof r.data?.percent_off === "number" && r.data.percent_off > 0) {
        setDraftPct(String(r.data.percent_off));
      }
      if (r.data?.label) setDraftLabel(r.data.label);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/admin/price/promo", patch);
      setState(r.data);
      toast.success(patch.active === false ? "Promo désactivée" : "Promo mise à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async () => {
    const nextActive = !state?.active;
    await update({
      active: nextActive,
      percent_off: Math.max(0, Math.min(90, parseInt(draftPct, 10) || 0)),
      label: draftLabel,
    });
  };

  if (loading) {
    return (
      <div className="rounded-2xl bg-white p-6 text-center text-sm text-foreground/60 street-border-thin street-shadow-sm">
        Chargement…
      </div>
    );
  }

  const active = !!state?.active;
  const pct = state?.percent_off || 0;

  return (
    <div className="space-y-3" data-testid="admin-promo-tab">
      {/* État courant */}
      <div
        className={
          "rounded-2xl p-4 street-border-thin street-shadow-sm " +
          (active ? "bg-gradient-to-br from-primary to-red-600 text-white" : "bg-white")
        }
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-[Outfit] font-black uppercase tracking-widest opacity-80">
              État actuel
            </div>
            <div className="font-[Outfit] text-[22px] font-black mt-0.5">
              {active ? `-${pct}% ACTIVE` : "Aucune promo"}
            </div>
            {active && state?.label && (
              <div className="text-[12px] font-[Outfit] font-bold opacity-90 mt-1">
                « {state.label} »
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={toggle}
            disabled={saving}
            data-testid="admin-promo-toggle"
            className={
              "rounded-xl px-4 py-2.5 font-[Outfit] text-[13px] font-black uppercase tracking-widest street-border-thin street-press " +
              (active ? "bg-white text-primary" : "bg-primary text-primary-foreground")
            }
          >
            {active ? "Désactiver" : "Activer"}
          </button>
        </div>
      </div>

      {/* Config */}
      <div className="rounded-2xl bg-white p-4 street-border-thin street-shadow-sm space-y-3">
        <div>
          <label className="text-[10px] font-[Outfit] font-black uppercase tracking-widest text-foreground/60">
            Pourcentage de réduction (0 à 90)
          </label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="90"
              value={draftPct}
              onChange={(e) => setDraftPct(e.target.value)}
              data-testid="admin-promo-percent"
              className="flex-1 rounded-xl bg-white px-3 py-2 font-[Outfit] font-black street-border-thin outline-none focus:ring-4 focus:ring-primary/25"
              style={{ fontSize: "16px" }}
            />
            <span className="font-[Outfit] text-[18px] font-black">%</span>
          </div>
          <div className="mt-1 flex gap-1 flex-wrap">
            {[10, 15, 20, 30, 50].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setDraftPct(String(v))}
                className="rounded-lg bg-neutral-100 px-2 py-0.5 text-[11px] font-[Outfit] font-black street-border-thin street-press"
              >
                -{v}%
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] font-[Outfit] font-black uppercase tracking-widest text-foreground/60">
            Libellé visible par les passagers
          </label>
          <input
            type="text"
            maxLength={120}
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            data-testid="admin-promo-label"
            placeholder="Ex. Happy hour -20% jusqu'à minuit"
            className="mt-1 w-full rounded-xl bg-white px-3 py-2 font-[Outfit] font-medium street-border-thin outline-none focus:ring-4 focus:ring-primary/25"
            style={{ fontSize: "16px" }}
          />
          <div className="mt-1 text-[10px] text-foreground/50 font-bold">
            Affiché en bannière rouge en haut de l'app passager
          </div>
        </div>

        <button
          type="button"
          onClick={() =>
            update({
              percent_off: Math.max(0, Math.min(90, parseInt(draftPct, 10) || 0)),
              label: draftLabel,
            })
          }
          disabled={saving}
          data-testid="admin-promo-save"
          className="w-full rounded-xl bg-black px-4 py-2.5 text-[12px] font-black text-white street-border-thin street-shadow-sm street-press disabled:opacity-50"
        >
          <Save className="inline h-3.5 w-3.5 mr-1 -mt-0.5" /> Enregistrer (sans activer)
        </button>
      </div>

      {/* Preview */}
      <div className="rounded-2xl bg-neutral-50 p-4 street-border-thin">
        <div className="text-[10px] font-[Outfit] font-black uppercase tracking-widest text-foreground/60 mb-2">
          Aperçu côté passager
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-3 py-1.5 street-border-thin">
          <span className="text-[11px] font-[Outfit] font-black uppercase tracking-widest">🔥 Promo</span>
          <span className="text-[13px] font-[Outfit] font-black">
            -{Math.max(0, Math.min(90, parseInt(draftPct, 10) || 0))}%
          </span>
          <span className="text-[11px] font-[Outfit] font-bold opacity-90 truncate max-w-[180px]">
            {draftLabel || "—"}
          </span>
        </div>
        <div className="mt-2 text-[11px] text-foreground/60">
          Les passagers voient ce bandeau tant que la promo est <b>active</b>, et un prix barré sur chaque course.
        </div>
      </div>
    </div>
  );
}


/**
 * Admin — Transferts carte en attente de payout.
 *
 * Si à la complétion d'une course payée par carte, le compte Connect du
 * covoitureur n'était pas prêt (onboarding Stripe incomplet, flag
 * `charges_enabled` à false), le transfer est skip et l'argent reste
 * sur SAOR. Cet onglet liste ces courses "orphelines" et permet à
 * l'admin de rejouer le transfer dès que le covoitureur a finalisé son
 * compte. Protection stricte contre les doubles transferts côté
 * backend (409 si `stripe_transfer_id` déjà posé).
 */
function PendingTransfersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replayingId, setReplayingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/admin/price/transfers/pending");
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const replay = async (requestId) => {
    setReplayingId(requestId);
    try {
      const r = await api.post(`/admin/price/transfers/${requestId}/replay`);
      if (r.data?.ok) {
        toast.success(`Transfer envoyé — ${r.data.transferred_eur}€`);
      } else {
        toast.error(
          r.data?.detail ||
          "Transfer non effectué — le covoitureur doit finaliser son onboarding Stripe"
        );
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReplayingId(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl bg-white p-6 text-center text-sm text-slate-500 shadow-sm ring-1 ring-black/5">
        Chargement…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-black/5" data-testid="pending-transfers-empty">
        <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500 mb-2" strokeWidth={2.5} />
        <div className="font-[Outfit] text-[14px] font-bold text-slate-900 mb-1">
          Aucun transfer en attente
        </div>
        <div className="text-[12px] text-slate-500">
          Toutes les courses carte terminées ont été payées aux covoitureurs ✅
        </div>
      </div>
    );
  }

  const totalDue = items.reduce((s, it) => s + Number(it.net_to_driver_eur || 0), 0);

  return (
    <div className="space-y-2.5" data-testid="pending-transfers-tab">
      <div className="rounded-2xl bg-amber-50 p-3.5 shadow-sm ring-1 ring-amber-200">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600" strokeWidth={2.5} />
          <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-800">
            {items.length} course{items.length > 1 ? "s" : ""} · {totalDue.toFixed(2)}€ à transférer
          </div>
        </div>
        <div className="mt-1 text-[11.5px] text-amber-900/80 leading-snug">
          Ces transferts ont été skippés car le covoitureur n'avait pas encore activé son compte Stripe Connect.
          Clique "Rejouer" dès que l'onboarding est fini.
        </div>
      </div>

      {items.map((it) => {
        const ready = it.driver_stripe_charges_enabled;
        return (
          <div
            key={it.request_id}
            className="rounded-2xl bg-white p-3.5 shadow-sm ring-1 ring-black/5"
            data-testid={`pending-transfer-${it.request_id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-[Outfit] text-[18px] font-bold text-primary tabular-nums">
                {Number(it.net_to_driver_eur || 0).toFixed(2)}€
              </div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                {it.commission_pct}% commission
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 p-2.5 mb-2">
              <div className="text-[12px] font-[Outfit] font-semibold text-slate-800 truncate">
                {it.from_address}
              </div>
              <div className="text-[12px] font-[Outfit] font-semibold text-slate-800 truncate">
                → {it.to_address}
              </div>
              <div className="mt-1 text-[10.5px] text-slate-500">
                Prix brut : {Number(it.price_eur || 0).toFixed(2)}€ · Course {it.request_id.slice(0, 8)}
              </div>
            </div>

            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Covoitureur
                </div>
                <div className="text-[12.5px] font-[Outfit] font-bold text-slate-900 truncate">
                  {it.driver_name || it.driver_email || it.driver_user_id}
                </div>
                <div className="text-[11px] text-slate-500 font-mono truncate">
                  {it.driver_stripe_account_id || "—"}
                </div>
              </div>
              <span
                className={
                  "shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold " +
                  (ready
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-red-100 text-red-800")
                }
              >
                <span className={"h-1.5 w-1.5 rounded-full " + (ready ? "bg-emerald-500" : "bg-red-500")} />
                {ready ? "Compte prêt" : "Onboarding incomplet"}
              </span>
            </div>

            <button
              type="button"
              disabled={!ready || replayingId === it.request_id}
              onClick={() => replay(it.request_id)}
              data-testid={`pending-transfer-replay-${it.request_id}`}
              className={
                "w-full rounded-xl px-4 py-2.5 text-[12.5px] font-bold shadow-sm active:scale-[0.99] transition disabled:opacity-50 disabled:cursor-not-allowed " +
                (ready
                  ? "bg-primary text-primary-foreground"
                  : "bg-slate-200 text-slate-500")
              }
            >
              {replayingId === it.request_id
                ? "Envoi en cours…"
                : ready
                ? `Rejouer le transfer (${Number(it.net_to_driver_eur || 0).toFixed(2)}€)`
                : "En attente de l'onboarding Stripe"}
            </button>
          </div>
        );
      })}
    </div>
  );
}



function PricingTab() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/price/pricing-config");
        setCfg(r.data.current);
        setDefaults(r.data.defaults);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !cfg) {
    return (
      <div className="rounded-2xl bg-white p-6 text-center text-sm text-foreground/60 street-border-thin street-shadow-sm">
        Chargement…
      </div>
    );
  }

  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/price/pricing-config", {
        base_eur: Number(cfg.base_eur),
        per_km_eur: Number(cfg.per_km_eur),
        min_fare_eur: Number(cfg.min_fare_eur),
        commission_pct: Number(cfg.commission_pct),
        live_default_radius_km: Number(cfg.live_default_radius_km ?? 25),
        toll_eur_per_km: Number(cfg.toll_eur_per_km ?? 0.11),
        scheduled_discount_pct: Number(cfg.scheduled_discount_pct ?? 20),
        scheduled_degressive_lead_hours: Number(cfg.scheduled_degressive_lead_hours ?? 5),
        degressive_from_km: Number(cfg.degressive_from_km ?? 80),
        degressive_per_km_eur: Number(cfg.degressive_per_km_eur ?? 0.11),
        negotiation_from_km: Number(cfg.negotiation_from_km ?? 80),
      });
      setCfg(r.data);
      toast.success("Tarifs mis à jour ✅");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => setCfg({ ...defaults });

  // Live preview for a 10 km ride, 1 passenger
  const previewKm = 10;
  const total = cfg.base_eur + previewKm * cfg.per_km_eur;

  return (
    <div className="space-y-3">
      <PriceKnob
        label="Prix par kilomètre"
        unit="€ / km"
        value={cfg.per_km_eur}
        onChange={set("per_km_eur")}
        min={0}
        max={5}
        step={0.05}
        tone="primary"
        testid="admin-pricing-per-km"
      />
      <PriceKnob
        label="Frais de base"
        unit="€ fixes"
        value={cfg.base_eur}
        onChange={set("base_eur")}
        min={0}
        max={20}
        step={0.5}
        tone="accent"
        testid="admin-pricing-base"
      />
      <PriceKnob
        label="Prix minimum"
        unit="€"
        value={cfg.min_fare_eur}
        onChange={set("min_fare_eur")}
        min={0}
        max={20}
        step={0.5}
        testid="admin-pricing-min"
      />
      <PriceKnob
        label="Commission Price"
        unit="% par course"
        value={cfg.commission_pct ?? 25}
        onChange={set("commission_pct")}
        min={0}
        max={100}
        step={1}
        tone="primary"
        testid="admin-pricing-commission"
      />
      <PriceKnob
        label="Rayon live par défaut"
        unit="km max entre passager et covoitureur"
        value={cfg.live_default_radius_km ?? 25}
        onChange={set("live_default_radius_km")}
        min={1}
        max={200}
        step={1}
        tone="accent"
        testid="admin-pricing-live-radius"
      />
      <PriceKnob
        label="Péages estimés (trajets ≥ 50 km)"
        unit="€ par km d'autoroute payante"
        value={cfg.toll_eur_per_km ?? 0.11}
        onChange={set("toll_eur_per_km")}
        min={0}
        max={0.3}
        step={0.01}
        tone="accent"
        testid="admin-pricing-toll-per-km"
      />
      <PriceKnob
        label="Programmée : réduction sur le tarif immédiat"
        unit="%"
        value={cfg.scheduled_discount_pct ?? 20}
        onChange={set("scheduled_discount_pct")}
        min={0}
        max={90}
        step={1}
        tone="primary"
        testid="admin-pricing-scheduled-discount"
      />
      <PriceKnob
        label="Tarif dégressif à partir de (délai de réservation)"
        unit="heures à l'avance"
        value={cfg.scheduled_degressive_lead_hours ?? 5}
        onChange={set("scheduled_degressive_lead_hours")}
        min={1}
        max={72}
        step={1}
        tone="accent"
        testid="admin-pricing-degressive-lead"
      />
      <PriceKnob
        label="Dégressif : prix plein jusqu'à"
        unit="km"
        value={cfg.degressive_from_km ?? 80}
        onChange={set("degressive_from_km")}
        min={0}
        max={300}
        step={5}
        tone="primary"
        testid="admin-pricing-degressive-from-km"
      />
      <PriceKnob
        label="Dégressif : prix des km suivants"
        unit="€ / km"
        value={cfg.degressive_per_km_eur ?? 0.11}
        onChange={set("degressive_per_km_eur")}
        min={0}
        max={1}
        step={0.01}
        tone="accent"
        testid="admin-pricing-degressive-per-km"
      />
      <PriceKnob
        label="Prix libre + négociation à partir de"
        unit="km (demandes et trajets publiés)"
        value={cfg.negotiation_from_km ?? 80}
        onChange={set("negotiation_from_km")}
        min={10}
        max={500}
        step={5}
        tone="primary"
        testid="admin-pricing-negotiation-km"
      />

      <div className="rounded-2xl bg-secondary/40 p-4 street-border-thin">
        <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
          Aperçu — 10 km · 1 passager
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-[Outfit] text-[28px] font-black text-primary">{total.toFixed(2)}€</span>
          <span className="text-[11px] font-bold text-foreground/50">tout compris</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          onClick={resetDefaults}
          className="street-press flex items-center justify-center gap-2 rounded-2xl bg-white py-3.5 font-[Outfit] text-[14px] font-black tracking-tight text-foreground street-border-thin street-shadow-sm"
          data-testid="admin-pricing-reset"
        >
          <RotateCcw className="h-4 w-4" />
          Défaut
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="street-press flex items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[14px] font-black tracking-tight text-primary-foreground street-border street-shadow-sm disabled:opacity-60"
          data-testid="admin-pricing-save"
        >
          <Save className="h-4 w-4" />
          {saving ? "…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

function PriceKnob({ label, unit, value, onChange, min, max, step, tone, testid }) {
  const bg = tone === "primary" ? "bg-primary text-primary-foreground" : tone === "accent" ? "bg-accent text-accent-foreground" : "bg-white text-foreground";
  const dec = () => onChange(Math.max(min, +(value - step).toFixed(2)));
  const inc = () => onChange(Math.min(max, +(value + step).toFixed(2)));
  return (
    <div className={`rounded-2xl ${bg} p-3.5 street-border-thin street-shadow-sm`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest opacity-80">{label}</div>
          <div className="mt-0.5 font-[Outfit] text-[22px] font-black leading-none">
            {Number(value || 0).toFixed(2)} <span className="text-[13px] opacity-80 font-medium">{unit}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={dec}
            className="street-press grid h-9 w-9 place-items-center rounded-xl bg-white/95 text-foreground street-border-thin text-[16px] font-black"
            data-testid={`${testid}-dec`}
          >
            −
          </button>
          <button
            type="button"
            onClick={inc}
            className="street-press grid h-9 w-9 place-items-center rounded-xl bg-white/95 text-foreground street-border-thin text-[16px] font-black"
            data-testid={`${testid}-inc`}
          >
            +
          </button>
        </div>
      </div>
      <input
        type="range"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        step={step}
        className="mt-2 w-full accent-white"
        aria-label={label}
        data-testid={`${testid}-slider`}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tab: Notifications                                                  */
/* ------------------------------------------------------------------ */
function AdminMyPushPrefsCard() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/price/notif-prefs");
      setPrefs(data?.prefs || { new_user: true, support: true });
    } catch (_e) {
      setPrefs({ new_user: true, support: true });
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key) => {
    if (!prefs || busy) return;
    const next = !prefs[key];
    setPrefs((p) => ({ ...p, [key]: next }));
    setBusy(true);
    try {
      const { data } = await api.put("/admin/price/notif-prefs", { [key]: next });
      setPrefs(data?.prefs || { ...prefs, [key]: next });
      toast.success(next ? "Notifs activées" : "Notifs coupées");
    } catch (err) {
      // Rollback en cas d'erreur.
      setPrefs((p) => ({ ...p, [key]: !next }));
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!prefs) {
    return (
      <div className="rounded-2xl bg-white p-4 street-border street-shadow-sm animate-pulse">
        <div className="h-4 w-24 rounded bg-black/10" />
        <div className="mt-3 h-8 w-full rounded bg-black/10" />
      </div>
    );
  }

  const on = prefs.new_user !== false;
  const supportOn = prefs.support !== false;

  return (
    <div className="space-y-3">
      <div
        className="rounded-2xl bg-white p-4 street-border street-shadow-sm"
        data-testid="admin-my-push-prefs"
      >
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-accent street-border-thin">
            <Bell className="h-5 w-5 text-accent-foreground" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-[Outfit] text-[14px] font-black leading-tight">
              Notifs nouveaux utilisateurs
            </div>
            <div className="mt-0.5 text-[11px] font-medium text-foreground/60 leading-snug">
              Reçois un push à chaque inscription client ou covoitureur.
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            onClick={() => toggle("new_user")}
            disabled={busy}
            data-testid="admin-toggle-new-user"
            className={
              "relative inline-flex h-8 w-14 flex-none items-center rounded-full street-border-thin transition-colors " +
              (on ? "bg-emerald-500" : "bg-muted")
            }
          >
            <span
              className={
                "inline-block h-6 w-6 transform rounded-full bg-white street-border-thin shadow-sm transition-transform " +
                (on ? "translate-x-7" : "translate-x-1")
              }
            />
          </button>
        </div>
      </div>

      <div
        className="rounded-2xl bg-white p-4 street-border street-shadow-sm"
        data-testid="admin-support-push-prefs"
      >
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-primary/15 street-border-thin">
            <MessageCircle className="h-5 w-5 text-primary" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-[Outfit] text-[14px] font-black leading-tight">
              Notifs messages support
            </div>
            <div className="mt-0.5 text-[11px] font-medium text-foreground/60 leading-snug">
              Push instantané dès qu'un utilisateur envoie un message support.
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={supportOn}
            onClick={() => toggle("support")}
            disabled={busy}
            data-testid="admin-toggle-support"
            className={
              "relative inline-flex h-8 w-14 flex-none items-center rounded-full street-border-thin transition-colors " +
              (supportOn ? "bg-emerald-500" : "bg-muted")
            }
          >
            <span
              className={
                "inline-block h-6 w-6 transform rounded-full bg-white street-border-thin shadow-sm transition-transform " +
                (supportOn ? "translate-x-7" : "translate-x-1")
              }
            />
          </button>
        </div>
      </div>
    </div>
  );
}


function PushDiagnosticCard() {
  const [target, setTarget] = useState("drivers");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/price/push/diagnostic?target=${target}`);
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };
  return (
    <div className="rounded-2xl bg-white p-4 street-border-thin" data-testid="push-diagnostic">
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-[Outfit] text-[15px] font-black">Qui reçoit les notifs ?</div>
        <select value={target} onChange={(e) => { setTarget(e.target.value); setData(null); }} className="rounded-xl px-2 py-1.5 text-[12px] font-bold street-border-thin" data-testid="push-diagnostic-target">
          <option value="drivers">Conducteurs</option>
          <option value="clients">Passagers</option>
        </select>
        <button onClick={run} disabled={loading} className="street-press inline-flex items-center gap-1 rounded-xl bg-primary px-3 py-1.5 text-[12px] font-black text-primary-foreground street-border-thin" data-testid="push-diagnostic-run">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />} Vérifier
        </button>
      </div>
      {data && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-secondary/40 p-2"><div className="text-[18px] font-black" data-testid="push-diag-total">{data.total}</div><div className="text-[10px] font-bold uppercase">Total</div></div>
            <div className="rounded-xl bg-green-100 p-2"><div className="text-[18px] font-black text-green-700" data-testid="push-diag-ok">{data.reachable_count}</div><div className="text-[10px] font-bold uppercase">Reçoivent</div></div>
            <div className="rounded-xl bg-red-100 p-2"><div className="text-[18px] font-black text-red-700" data-testid="push-diag-ko">{data.unreachable_count}</div><div className="text-[10px] font-bold uppercase">Ne reçoivent pas</div></div>
          </div>
          {data.unreachable_count > 0 && (
            <details open className="rounded-xl bg-red-50 p-2">
              <summary className="cursor-pointer text-[12px] font-black">Sans appareil abonné (notifs refusées, app jamais ouverte ou ancienne version)</summary>
              <ul className="mt-1 max-h-48 overflow-y-auto text-[12px]">
                {data.unreachable.map((u) => <li key={u.id} className="truncate py-0.5">{u.name || u.email}{u.dual_role ? " · double rôle" : ""}{u.error ? " · erreur API" : ""}</li>)}
              </ul>
            </details>
          )}
          <details className="rounded-xl bg-green-50 p-2">
            <summary className="cursor-pointer text-[12px] font-black">Abonnés ({data.reachable_count})</summary>
            <ul className="mt-1 max-h-48 overflow-y-auto text-[12px]">
              {data.reachable.map((u) => <li key={u.id} className="truncate py-0.5">{u.name || u.email} · {u.devices} appareil{u.devices > 1 ? "s" : ""} ({u.platforms.map((p) => p.replace("Push", "")).join(", ")}){u.dual_role ? " · double rôle" : ""}</li>)}
            </ul>
          </details>
        </div>
      )}
    </div>
  );
}

function NotifTab() {
  const [form, setForm] = useState({
    title: "",
    body: "",
    target: "all",
    sound: "system",
  });
  const [targetUser, setTargetUser] = useState(null);
  const [sending, setSending] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Titre et message obligatoires");
      return;
    }
    if (form.target === "user" && !targetUser) {
      toast.error("Sélectionne un utilisateur");
      return;
    }
    setSending(true);
    try {
      const payload = { ...form };
      if (form.target === "user") payload.target_user_id = targetUser.id;
      const r = await api.post("/admin/price/notifications/broadcast", payload);
      toast.success(`Envoyée à ${r.data.sent_to} utilisateur${r.data.sent_to > 1 ? "s" : ""} ✅`);
      setForm((f) => ({ ...f, title: "", body: "" }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const testSound = () => {
    if (form.sound === "price") playPriceChime();
    else if (form.sound === "system") {
      // Best-effort: emit a soft beep via oscillator
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        o.type = "sine";
        o.frequency.value = 880;
        const g = ctx.createGain();
        g.gain.value = 0.15;
        o.connect(g).connect(ctx.destination);
        o.start();
        setTimeout(() => { o.stop(); ctx.close(); }, 180);
      } catch (_e) { /* Web Audio blocked */ }
    }
    // discreet = no sound at all
  };

  return (
    <div className="space-y-3">
      <PushDiagnosticCard />
      <AdminMyPushPrefsCard />

      <TemplateBar
        kind="notif"
        current={form}
        onLoad={(t) => setForm((f) => ({
          ...f,
          title: t.title,
          body: t.body,
          sound: t.sound || f.sound,
        }))}
      />

      <div className="rounded-2xl bg-white p-4 street-border street-shadow-sm">
        <span className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-foreground/70">Titre</span>
        <input
          type="text"
          value={form.title}
          onChange={upd("title")}
          placeholder="Ex. Nouveauté Price !"
          data-testid="admin-notif-title"
          className="w-full rounded-xl bg-secondary/40 px-3 py-2.5 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:bg-white focus:ring-4 focus:ring-primary/25"
          style={{ fontSize: "16px" }}
        />
        <span className="mt-3 mb-1.5 block text-[11px] font-black uppercase tracking-widest text-foreground/70">Message</span>
        <textarea
          value={form.body}
          onChange={upd("body")}
          rows={3}
          placeholder="Le message qui va s'afficher en notif…"
          data-testid="admin-notif-body"
          className="w-full rounded-xl bg-secondary/40 px-3 py-2.5 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:bg-white focus:ring-4 focus:ring-primary/25 resize-none"
          style={{ fontSize: "16px" }}
        />
      </div>

      <div className="rounded-2xl bg-white p-3 street-border street-shadow-sm">
        <div className="text-[11px] font-black uppercase tracking-widest text-foreground/70 mb-2">Cible</div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { v: "all", label: "Tous" },
            { v: "clients", label: "Pax" },
            { v: "drivers", label: "Covoit." },
            { v: "user", label: "1 user" },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setForm((f) => ({ ...f, target: o.v }))}
              className={`street-press rounded-xl py-2 text-[11px] font-black uppercase tracking-widest street-border-thin ${form.target === o.v ? "bg-primary text-primary-foreground" : "bg-white text-foreground"}`}
              data-testid={`admin-notif-target-${o.v}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {form.target === "user" && (
          <div className="mt-2">
            <UserPicker value={targetUser} onChange={setTargetUser} label="Utilisateur ciblé" />
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white p-3 street-border street-shadow-sm">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-black uppercase tracking-widest text-foreground/70">Son</div>
          <button
            type="button"
            onClick={testSound}
            className="street-press inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-accent-foreground street-border-thin"
            data-testid="admin-notif-test-sound"
          >
            <Volume2 className="h-3 w-3" />
            Tester
          </button>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <SoundOption
            value="discreet"
            label="Discret"
            Icon={VolumeX}
            active={form.sound === "discreet"}
            onClick={() => setForm((f) => ({ ...f, sound: "discreet" }))}
          />
          <SoundOption
            value="system"
            label="Système"
            Icon={Volume2}
            active={form.sound === "system"}
            onClick={() => setForm((f) => ({ ...f, sound: "system" }))}
          />
          <SoundOption
            value="price"
            label="Price"
            Icon={Music}
            active={form.sound === "price"}
            onClick={() => setForm((f) => ({ ...f, sound: "price" }))}
          />
        </div>
      </div>

      <button
        onClick={send}
        disabled={sending}
        className="street-press flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-[Outfit] text-[15px] font-black tracking-tight text-primary-foreground street-border street-shadow disabled:opacity-60"
        data-testid="admin-notif-send"
      >
        <Send className="h-4 w-4" />
        {sending ? "Envoi…" : "Envoyer la notification"}
      </button>
      <ScheduledNotifsPanel renderUserPicker={(v, on) => <UserPicker value={v} onChange={on} label="Utilisateur ciblé" />} />
    </div>
  );
}

function SoundOption({ label, Icon, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`admin-notif-sound-${label.toLowerCase()}`}
      className={`street-press flex flex-col items-center gap-1 rounded-xl py-2.5 street-border-thin ${
        active ? "bg-primary text-primary-foreground" : "bg-white text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" strokeWidth={2.5} />
      <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Tab: Chats — modération des discussions live                       */
/* ────────────────────────────────────────────────────────────────── */
function ChatsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReq, setOpenReq] = useState(null);
  // Garde en mémoire la dernière date vue par conversation pour
  // mettre en évidence les nouveaux messages (badge "NOUVEAU").
  const seenRef = useRef({});
  const [bumpTick, setBumpTick] = useState(0);

  const load = React.useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const r = await api.get("/admin/price/chats");
      const next = Array.isArray(r.data?.items) ? r.data.items : [];
      // Au premier load (silent=false), on marque tout comme déjà vu
      // pour éviter que toute la liste s'allume en "NOUVEAU".
      if (!silent) {
        const seed = {};
        for (const it of next) { if (it?.request_id) seed[it.request_id] = it.last_at; }
        seenRef.current = seed;
      }
      setItems(next);
    } catch (e) {
      if (!silent) toast.error(formatApiError(e, "Impossible de charger les chats"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    // Refresh toutes les 4s en silencieux pour un vrai feeling "live"
    // sans faire clignoter le spinner à chaque tick.
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      load(true);
    }, 4000);
    // Re-render toutes les 30s pour rafraîchir les badges "EN DIRECT"
    // (basés sur la fraîcheur du dernier message).
    const tick = setInterval(() => setBumpTick((x) => x + 1), 30000);
    return () => { clearInterval(t); clearInterval(tick); };
  }, [load]);

  const fmtRelative = (iso) => {
    if (!iso) return "—";
    try {
      const diff = (Date.now() - new Date(iso).getTime()) / 1000;
      if (diff < 60) return `il y a ${Math.max(1, Math.floor(diff))} s`;
      if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
      return new Date(iso).toLocaleDateString("fr-FR");
    } catch { return iso; }
  };

  const statusBadge = (s) => {
    if (s === "accepted") return { label: "En cours", cls: "bg-[#D6F5D6]" };
    if (s === "completed") return { label: "Terminée", cls: "bg-neutral-200" };
    if (s === "trip") return { label: "Trajet publié", cls: "bg-[#FFF3CC]" };
    if (s === "trip_closed") return { label: "Trajet clos", cls: "bg-neutral-200" };
    return { label: "Annulée", cls: "bg-[#FFE5E5]" };
  };

  return (
    <div className="space-y-3" data-testid="admin-chats-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-[Outfit] text-[18px] font-black">Chats live</div>
          <div className="text-[12px] text-foreground/60 font-medium">
            {items.length} conversation{items.length > 1 ? "s" : ""} · temps réel
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="street-press h-9 w-9 rounded-xl bg-white grid place-items-center street-border-thin street-shadow-sm"
          data-testid="admin-chats-refresh"
        >
          <RotateCw className={"h-4 w-4 " + (loading ? "animate-spin" : "")} strokeWidth={2.5} />
        </button>
      </div>

      {loading && items.length === 0 && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="rounded-2xl bg-white p-6 text-center street-border-thin">
          <MessageCircle className="h-8 w-8 text-foreground/30 mx-auto mb-2" strokeWidth={2} />
          <p className="font-[Outfit] text-[14px] font-medium text-foreground/60">
            Aucun chat pour l'instant. Ils apparaîtront ici dès qu'un covoitureur accepte une course.
          </p>
        </div>
      )}
      {items.map((it) => {
        const b = statusBadge(it.status);
        // Fraîcheur : "EN DIRECT" si dernier msg < 60s, et accepted.
        let lastMs = 0;
        try { lastMs = it.last_at ? new Date(it.last_at).getTime() : 0; } catch { /* noop */ }
        const freshness = lastMs ? (Date.now() - lastMs) / 1000 : Infinity;
        const isLive = (it.status === "accepted" || it.status === "trip") && freshness < 60;
        // "NOUVEAU" si on a déjà vu cette conversation avec une date
        // antérieure. Marque automatiquement comme vue quand l'admin
        // ouvre la conv (via setOpenReq).
        const prevSeen = seenRef.current[it.request_id];
        const isNew = prevSeen && it.last_at && prevSeen !== it.last_at;
        return (
          <button
            key={it.request_id}
            type="button"
            onClick={() => {
              seenRef.current[it.request_id] = it.last_at;
              setOpenReq(it);
            }}
            data-testid={`admin-chat-open-${it.request_id}`}
            className={
              "street-press w-full text-left rounded-2xl bg-white p-3 street-border-thin street-shadow-sm space-y-2 " +
              (isNew ? "ring-2 ring-[#FF3B30]/70" : "")
            }
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={"text-[10px] font-[Outfit] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-black " + b.cls}>
                  {b.label}
                </span>
                {isLive && (
                  <span className="text-[10px] font-[Outfit] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-black bg-[#FF3B30] text-white flex items-center gap-1">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                    En direct
                  </span>
                )}
                {isNew && !isLive && (
                  <span className="text-[10px] font-[Outfit] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-black bg-[#FFE066]">
                    Nouveau
                  </span>
                )}
                <span className="text-[10px] font-[Outfit] font-black text-foreground/60">
                  {it.count} msg
                </span>
              </div>
              <div className="text-[11px] font-medium text-foreground/60">{fmtRelative(it.last_at)}</div>
            </div>
            <div className="text-[13px] font-[Outfit] font-black text-black leading-tight truncate">
              {it.client_name} ↔ {it.driver_name}
            </div>
            <div className="text-[12px] text-foreground/70 font-medium truncate">
              {it.from_address} → {it.to_address}
            </div>
            <div className="text-[12px] text-foreground/85 truncate flex items-center gap-1">
              <span className={
                "text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border border-black " +
                (it.last_sender_role === "admin" ? "bg-[#FFF3CC]" : it.last_sender_role === "driver" ? "bg-[#D6F5D6]" : "bg-[#FFE5E5]")
              }>
                {it.last_sender_role}
              </span>
              <span className="truncate">{it.last_text}</span>
            </div>
          </button>
        );
      })}

      <ChatSheet
        open={!!openReq}
        onClose={() => setOpenReq(null)}
        requestId={openReq?.request_id}
        selfRole="admin"
        counterpartName={openReq ? `${openReq.client_name} ↔ ${openReq.driver_name}` : ""}
        apiBaseOverride={openReq?.kind === "trip" ? `/admin/price/trip-threads/${openReq.request_id}/messages` : undefined}
        subtitle={openReq?.kind === "trip" ? "Trajet publié · avant réservation" : undefined}
      />
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────── */
/* Tab: Live — courses en cours + demandes en attente                 */
/* ────────────────────────────────────────────────────────────────── */
function LiveRidesTab() {
  const { data, loading, refreshing, reload } = useAdminFetch(
    "admin:price:rides:live",
    async () => {
      const r = await api.get("/admin/price/rides/live");
      return {
        active: r.data?.active || [],
        waiting: r.data?.waiting || [],
        trips: r.data?.trips || [],
        counts: r.data?.counts || { active: 0, waiting: 0, trips: 0 },
      };
    },
    { ttlMs: 4_000, pollMs: 8_000, initialData: { active: [], waiting: [], trips: [], counts: { active: 0, waiting: 0, trips: 0 } } },
  );
  const load = () => reload(true);
  const [assignFor, setAssignFor] = useState(null); // {kind:'driver'|'passenger', req|trip}

  const deleteRide = async (req, live) => {
    const label = req.client_name ? `de ${req.client_name}` : req.id.slice(0, 8);
    const confirmMsg = live
      ? `Supprimer la course en cours ${label} ? Le chauffeur et le client seront notifiés.`
      : `Supprimer cette demande ${label} ?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const url = `/admin/price/rides/${req.id}${live ? "?force=true" : ""}`;
      const r = await api.delete(url);
      const refunded = r.data?.refunded_eur || 0;
      toast.success(refunded > 0 ? `Course supprimée · passager remboursé ${refunded.toFixed(2)}€` : "Course supprimée · parties notifiées");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Suppression impossible"));
    }
  };

  const rebroadcast = async (req) => {
    if (!window.confirm(`Relancer la notif aux covoitureurs proches pour la demande de ${req.client_name || req.id.slice(0, 8)} ?`)) return;
    try {
      const r = await api.post(`/admin/price/rides/${req.id}/rebroadcast`);
      const n = r?.data?.notified_drivers ?? 0;
      if (n > 0) {
        toast.success(`🔔 Notif renvoyée à ${n} covoitureur${n > 1 ? "s" : ""}`);
      } else {
        toast.info(r?.data?.message || "Aucun covoitureur disponible à proximité");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Impossible de relancer la notif"));
    }
  };

  const Row = ({ req, live }) => {
    const payCfg = _paymentDisplay(req.payment_method, req.payment_status);
    const ageLabel = _ageSince(req.created_at);
    const isCancelled = req.status === "cancelled";
    return (
    <div
      className={
        "rounded-2xl p-3.5 street-border-thin street-shadow-sm " +
        (live ? "bg-white" : "bg-gradient-to-br from-white to-[#FFF8F1]")
      }
      data-testid={`admin-ride-${req.id}`}
    >
      {/* Header — status pill, payment, prix, actions */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={
            "text-[10px] font-[Outfit] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-black inline-flex items-center gap-1 " +
            (live
              ? (req.status === "in_progress" ? "bg-[#D6F5D6]" : req.status === "passenger_boarding" ? "bg-[#FFE9CC]" : "bg-[#FFEAE5]")
              : "bg-[#FFF3CC]")
          }>
            {live && <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />}
            {live ? (req.status === "in_progress" ? "En cours" : req.status === "passenger_boarding" ? "Embarquement" : "Accepté") : (req.status === "pending" ? "En attente" : req.status)}
          </span>
          <span className={
            "text-[10px] font-[Outfit] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-black inline-flex items-center gap-1 " +
            payCfg.bg
          }>
            {payCfg.icon}
            {payCfg.label}
          </span>
          <span className="text-[10px] font-[Outfit] font-black text-foreground/60 inline-flex items-center gap-1">
            <Clock className="h-3 w-3" strokeWidth={2.8} />
            {ageLabel}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="font-[Outfit] text-[15px] font-black text-primary tabular-nums">{Number(req.price_eur || 0).toFixed(2)}€</div>
          {!live && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); rebroadcast(req); }}
                data-testid={`admin-ride-rebroadcast-${req.id}`}
                title="Relancer la notif aux covoitureurs proches"
                className="street-press h-7 w-7 rounded-lg grid place-items-center bg-accent border-2 border-black text-accent-foreground"
              >
                <RadioTower className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setAssignFor({ kind: "driver", target: req }); }}
                data-testid={`admin-ride-assign-${req.id}`}
                title="Attribuer un covoitureur"
                className="street-press h-7 w-7 rounded-lg grid place-items-center bg-primary border-2 border-black text-primary-foreground"
              >
                <UserCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); deleteRide(req, live); }}
            data-testid={`admin-ride-delete-${req.id}`}
            title={live ? "Forcer la suppression (course en cours)" : "Supprimer la demande"}
            className="street-press h-7 w-7 rounded-lg grid place-items-center bg-red-500 border-2 border-black text-white"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Trajet — adresses sur 2 lignes avec puce visuelle */}
      <div className="rounded-xl bg-neutral-50 p-2.5 street-border-thin mb-2">
        <div className="flex items-start gap-2">
          <span className="mt-1.5 inline-block h-2 w-2 rounded-full bg-primary flex-none" />
          <div className="min-w-0 flex-1 text-[12.5px] font-[Outfit] font-bold text-black truncate">
            {req.from_address}
          </div>
        </div>
        <div className="ml-[3px] my-0.5 h-3 w-[1px] bg-black/20" />
        <div className="flex items-start gap-2">
          <span className="mt-1.5 inline-block h-2 w-2 rounded-full bg-black flex-none" />
          <div className="min-w-0 flex-1 text-[12.5px] font-[Outfit] font-bold text-black truncate">
            {req.to_address}
          </div>
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-[10px] font-[Outfit] font-black uppercase tracking-widest text-foreground/60">
          {req.distance_km != null && <span>{Number(req.distance_km).toFixed(1)} km</span>}
          <span>•</span>
          <span>{req.seats} pass.</span>
          {req.accept_two_wheels && (
            <>
              <span>•</span>
              <span className="text-primary">🏍️ 2 roues OK</span>
            </>
          )}
        </div>
      </div>

      {/* Parties — passager + covoitureur (si assigné) */}
      <div className={live && req.driver_name ? "grid grid-cols-2 gap-2" : ""}>
        <PartyCard
          role="client"
          name={req.client_name}
          phone={req.client_phone}
          photoUrl={req.client_photo_url}
          rating={req.client_rating}
          ridesCount={req.client_rides_count}
        />
        {live && req.driver_name && (
          <PartyCard
            role="driver"
            name={req.driver_name}
            phone={req.driver_phone}
            photoUrl={req.driver_photo_url}
            rating={req.driver_rating}
            ridesCount={req.driver_rides_count}
            car={req.driver_car}
            plate={req.driver_plate}
          />
        )}
      </div>

      {/* Notes passager — toujours visible si présentes */}
      {req.notes && req.notes.trim() && (
        <div className="mt-2 flex items-start gap-1.5 rounded-xl bg-[#FFF3CC] p-2 border border-black/15">
          <StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700" strokeWidth={2.5} />
          <div className="text-[11.5px] font-[Outfit] font-medium text-black leading-snug">
            {req.notes}
          </div>
        </div>
      )}

      {req.assigned_by_admin && (
        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-[Outfit] font-black text-primary border border-primary/30">
          🎯 Attribuée manuellement · {req.assigned_by_admin.split("@")[0]}
        </div>
      )}

      <NotifiedDriversButton requestId={req.id} />

      {/* Enrichissement live — n'apparaît que pour les courses actives */}
      {live && (
        <LiveRideDetails req={req} />
      )}
    </div>
  );
  };

  const TripRow = ({ t }) => (
    <div className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm" data-testid={`admin-trip-${t.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-[Outfit] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-black bg-[#E9E4FF]">
            Trajet publié
          </span>
          <span className="text-[10px] font-[Outfit] font-black text-foreground/60">
            {t.departure_date} · {t.departure_time}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="font-[Outfit] text-[13px] font-black">{Number(t.price_per_seat_eur || 0).toFixed(2)}€/place</div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setAssignFor({ kind: "passenger", target: t }); }}
            data-testid={`admin-trip-assign-${t.id}`}
            title="Attribuer un passager"
            disabled={t.seats_left <= 0}
            className="street-press h-7 w-7 rounded-lg grid place-items-center bg-primary border-2 border-black text-primary-foreground disabled:opacity-40"
          >
            <UserPlus className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        </div>
      </div>
      <div className="text-[13px] font-[Outfit] font-black text-black leading-tight truncate">
        {t.driver_name || "Covoitureur"} · {t.seats_left}/{t.seats_total} places restantes
      </div>
      <div className="text-[12px] text-foreground/70 font-medium truncate">
        {t.from_address} → {t.to_address}
      </div>
      {(t.booked_seats || []).length > 0 && (
        <div className="text-[10px] text-foreground/60 mt-1 truncate">
          👥 {t.booked_seats.map((s) => `${s.client_name || "?"} (${s.seats})`).join(" · ")}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-live-rides-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-[Outfit] text-[18px] font-black">Courses live</div>
          <div className="text-[12px] text-foreground/60 font-medium">
            {data.counts.active} en cours · {data.counts.waiting} en attente · refresh 8 s
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="street-press h-9 w-9 rounded-xl bg-white grid place-items-center street-border-thin street-shadow-sm"
          data-testid="admin-live-refresh"
        >
          <RotateCw className={"h-4 w-4 " + ((loading || refreshing) ? "animate-spin" : "")} strokeWidth={2.5} />
        </button>
      </div>

      <div>
        <div className="text-[11px] font-[Outfit] font-black uppercase tracking-widest text-foreground/70 mb-2">
          🟢 En cours ({data.counts.active})
        </div>
        <div className="space-y-2">
          {loading && <AdminRowSkeleton count={2} />}
          {!loading && data.active.length === 0 && (
            <div className="rounded-2xl bg-white p-4 text-center text-[13px] font-medium text-foreground/60 street-border-thin">
              Aucune course en cours
            </div>
          )}
          {data.active.map((r) => <Row key={r.id} req={r} live />)}
        </div>
      </div>

      <div>
        <div className="text-[11px] font-[Outfit] font-black uppercase tracking-widest text-foreground/70 mb-2">
          ⏳ Demandes en attente ({data.counts.waiting})
        </div>
        <div className="space-y-2">
          {loading && <AdminRowSkeleton count={2} />}
          {!loading && data.waiting.length === 0 && (
            <div className="rounded-2xl bg-white p-4 text-center text-[13px] font-medium text-foreground/60 street-border-thin">
              Aucune demande en attente
            </div>
          )}
          {data.waiting.map((r) => <Row key={r.id} req={r} live={false} />)}
        </div>
      </div>

      <div>
        <div className="text-[11px] font-[Outfit] font-black uppercase tracking-widest text-foreground/70 mb-2">
          🗓️ Trajets publiés ({data.counts.trips})
        </div>
        <div className="space-y-2">
          {loading && <AdminRowSkeleton count={2} />}
          {!loading && data.trips.length === 0 && (
            <div className="rounded-2xl bg-white p-4 text-center text-[13px] font-medium text-foreground/60 street-border-thin">
              Aucun trajet publié à venir
            </div>
          )}
          {data.trips.map((t) => <TripRow key={t.id} t={t} />)}
        </div>
      </div>

      {assignFor && (
        <AdminAssignModal
          config={assignFor}
          onClose={() => setAssignFor(null)}
          onDone={() => { setAssignFor(null); load(); }}
        />
      )}
    </div>
  );
}

function LiveRideDetails({ req }) {
  // Événements horodatés — on affiche uniquement ceux présents.
  const events = [
    { label: "Créée", at: req.created_at, icon: "🆕" },
    { label: "Acceptée", at: req.accepted_at, icon: "✅" },
    { label: "Embarquement", at: req.boarding_at, icon: "🧍" },
    { label: "Démarrée", at: req.started_at, icon: "🚗" },
  ].filter((e) => !!e.at);

  const chat = req.chat;
  const hasGps = req.driver_lat != null && req.driver_lng != null;
  const gpsAge = (() => {
    if (!req.driver_loc_at) return null;
    try {
      const t = new Date(req.driver_loc_at).getTime();
      const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
      if (sec < 60) return `il y a ${sec}s`;
      if (sec < 3600) return `il y a ${Math.round(sec / 60)}min`;
      return `il y a ${Math.round(sec / 3600)}h`;
    } catch { return null; }
  })();

  const mapsHref = hasGps
    ? `https://www.google.com/maps?q=${req.driver_lat},${req.driver_lng}`
    : null;

  return (
    <div className="mt-2 pt-2 border-t border-dashed border-black/15 space-y-1.5">
      {/* Ligne 1 — GPS + distance + ETA */}
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-[Outfit] font-black">
        {hasGps ? (
          <a
            href={mapsHref}
            target="_blank"
            rel="noreferrer"
            data-testid={`admin-ride-gps-${req.id}`}
            className="inline-flex items-center gap-1 rounded-full bg-[#D6F5D6] px-2 py-0.5 border border-black"
            onClick={(e) => e.stopPropagation()}
          >
            <MapPin className="h-3 w-3" strokeWidth={2.8} />
            GPS {gpsAge}
          </a>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-foreground/50">
            <MapPin className="h-3 w-3" strokeWidth={2.5} />
            GPS inconnu
          </span>
        )}
        {req.distance_remaining_km != null && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF3CC] px-2 py-0.5 border border-black">
            <ChevronRight className="h-3 w-3" strokeWidth={2.8} />
            {Number(req.distance_remaining_km).toFixed(1)} km
            {req.eta_minutes != null && ` · ~${req.eta_minutes} min`}
          </span>
        )}
      </div>

      {/* Ligne 2 — timeline des événements */}
      {events.length > 0 && (
        <div className="flex flex-wrap gap-1 text-[10px] font-medium text-foreground/60">
          {events.map((e, i) => (
            <span key={i} className="inline-flex items-center gap-1">
              <span>{e.icon}</span>
              <span className="font-[Outfit] font-black">{e.label}</span>
              <span>{_fmtTime(e.at)}</span>
              {i < events.length - 1 && <span className="opacity-30">→</span>}
            </span>
          ))}
        </div>
      )}

      {/* Ligne 3 — chat */}
      {chat && chat.last_text && (
        <div className="rounded-xl bg-black/5 px-2 py-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1 flex items-start gap-1.5">
              <MessageCircle className="h-3 w-3 mt-0.5 shrink-0 text-foreground/60" strokeWidth={2.8} />
              <div className="min-w-0">
                <div className="text-[10px] font-[Outfit] font-black text-foreground/80 truncate">
                  {chat.last_sender || "?"}{chat.last_role === "admin" ? " (admin)" : ""}
                </div>
                <div className="text-[11px] text-foreground/70 truncate">
                  {chat.last_text}
                </div>
              </div>
            </div>
            <span className="text-[10px] font-[Outfit] font-black text-foreground/50 shrink-0">
              {chat.count} msg · {_fmtTime(chat.last_at)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Petit helper d'affichage d'un timestamp ISO en HH:MM (local).
function _fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

// Format "il y a X" pour les timestamps récents (créée depuis).
function _ageSince(iso) {
  if (!iso) return "";
  try {
    const sec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (sec < 60) return `il y a ${sec}s`;
    if (sec < 3600) return `il y a ${Math.round(sec / 60)}min`;
    if (sec < 86400) return `il y a ${Math.round(sec / 3600)}h`;
    return `il y a ${Math.round(sec / 86400)}j`;
  } catch { return ""; }
}

// Affichage visuel du moyen de paiement — icône + label + couleur.
function _paymentDisplay(method, status) {
  if (method === "card") {
    const paid = status === "paid";
    return {
      icon: <CreditCard className="h-3 w-3" strokeWidth={2.8} />,
      label: paid ? "Carte · payée" : "Carte",
      bg: paid ? "bg-[#D6F5D6]" : "bg-[#DBE9FF]",
    };
  }
  return {
    icon: <Banknote className="h-3 w-3" strokeWidth={2.8} />,
    label: "Cash",
    bg: "bg-[#FFF3CC]",
  };
}

/**
 * Carte compacte d'une partie (passager ou covoitureur). Affiche photo
 * (ou initiales), nom, étoiles, nombre de courses + bouton d'appel
 * téléphone s'il est disponible. Pour le covoitureur on ajoute la
 * voiture et la plaque.
 */
function PartyCard({ role, name, phone, photoUrl, rating, ridesCount, car, plate }) {
  const isDriver = role === "driver";
  const safeName = name || (isDriver ? "Covoitureur" : "Passager");
  const initials = (safeName.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0] || "").join("") || "?").toUpperCase();
  const nStars = Math.max(0, Math.min(5, Math.round(Number(rating || 0))));
  const toneBg = isDriver ? "bg-[#FFE9CC]" : "bg-[#DBE9FF]";
  const toneFg = isDriver ? "text-amber-900" : "text-blue-900";
  return (
    <div className={"rounded-xl p-2 street-border-thin " + toneBg}>
      <div className="flex items-center gap-2">
        <div className="relative h-9 w-9 flex-none rounded-lg street-border-thin overflow-hidden bg-white grid place-items-center">
          {photoUrl ? (
            <img src={photoUrl} alt={safeName} className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <span className={"font-[Outfit] text-[12px] font-black " + toneFg}>{initials}</span>
          )}
          <span className={
            "absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full grid place-items-center border border-black " +
            (isDriver ? "bg-primary" : "bg-black")
          }>
            {isDriver ? <Car className="h-2 w-2 text-white" strokeWidth={3} /> : <Users className="h-2 w-2 text-white" strokeWidth={3} />}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-[Outfit] font-black uppercase tracking-widest text-foreground/60">
            {isDriver ? "Covoitureur" : "Passager"}
          </div>
          <div className="truncate text-[12.5px] font-[Outfit] font-black text-black leading-tight">
            {safeName}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-[10px] font-[Outfit] font-black text-foreground/70">
            <span className="inline-flex items-center">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={"h-2.5 w-2.5 " + (i < nStars ? "text-amber-500 fill-amber-500" : "text-black/15 fill-black/10")} strokeWidth={2} />
              ))}
            </span>
            {ridesCount != null && (
              <span className="opacity-70">· {ridesCount} course{ridesCount > 1 ? "s" : ""}</span>
            )}
          </div>
        </div>
        {phone && (
          <a
            href={`tel:${phone}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`party-call-${role}`}
            title={`Appeler ${safeName}`}
            className="street-press grid h-8 w-8 flex-none place-items-center rounded-lg bg-emerald-500 border-2 border-black text-white"
          >
            <Phone className="h-3.5 w-3.5" strokeWidth={2.5} />
          </a>
        )}
      </div>
      {isDriver && (car || plate) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[10px] font-[Outfit] font-black">
          {car && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 border border-black/15">
              <Car className="h-2.5 w-2.5" strokeWidth={2.5} />
              {car}
            </span>
          )}
          {plate && (
            <span className="inline-flex items-center rounded-full bg-black text-white px-2 py-0.5 font-mono tracking-widest">
              {plate}
            </span>
          )}
        </div>
      )}
    </div>
  );
}


function AdminAssignModal({ config, onClose, onDone }) {
  const kind = config.kind; // 'driver' | 'passenger'
  const target = config.target;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [seats, setSeats] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const path = kind === "driver" ? "drivers" : "clients";
        // Passe lat/lng du point de ramassage pour tri par distance côté backend.
        const params = new URLSearchParams({ q: query });
        if (kind === "driver" && target.from_coord?.lat != null && target.from_coord?.lng != null) {
          params.set("lat", target.from_coord.lat);
          params.set("lng", target.from_coord.lng);
        }
        const { data } = await api.get(`/admin/price/${path}/search?${params.toString()}`);
        setResults(data?.[path] || []);
      } catch (_e) { /* silencieux */ }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query, kind, target?.from_coord?.lat, target?.from_coord?.lng]);

  const pick = async (userItem) => {
    if (busy) return;
    setBusy(true);
    try {
      if (kind === "driver") {
        await api.post(`/admin/price/rides/${target.id}/assign-driver`, { driver_user_id: userItem.id });
        toast.success(`✅ ${userItem.name || userItem.email} attribué à cette course`);
      } else {
        await api.post(`/admin/price/trips/${target.id}/assign-passenger`, {
          client_user_id: userItem.id,
          seats: Number(seats) || 1,
        });
        toast.success(`✅ ${userItem.name || userItem.email} attribué à ce trajet`);
      }
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const title = kind === "driver" ? "Attribuer un covoitureur" : "Attribuer un passager";
  const sub = kind === "driver"
    ? `Demande de ${target.client_name} — ${target.from_address} → ${target.to_address}`
    : `Trajet ${target.driver_name} — ${target.from_address} → ${target.to_address} (${target.seats_left} places restantes)`;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-testid="admin-assign-modal">
      <div className="w-full max-w-lg rounded-t-3xl sm:rounded-3xl bg-white max-h-[92vh] overflow-y-auto street-border street-shadow">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-white px-4 py-3 border-b border-black/10">
          <div className="min-w-0 flex-1">
            <div className="font-[Outfit] text-[15px] font-black leading-tight">{title}</div>
            <div className="mt-0.5 text-[11px] font-medium text-foreground/60 truncate">{sub}</div>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 flex-none place-items-center rounded-full bg-neutral-100 street-border-thin street-press">
            ×
          </button>
        </div>
        <div className="p-4 space-y-3">
          {kind === "passenger" && (
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-foreground/60">
                Nombre de places
              </label>
              <input
                type="number"
                min={1}
                max={target.seats_left || 1}
                value={seats}
                onChange={(e) => setSeats(e.target.value)}
                className="input mt-1"
                data-testid="admin-assign-seats"
              />
            </div>
          )}
          <input
            type="text"
            placeholder={kind === "driver" ? "Recherche covoitureur (email, nom)" : "Recherche passager (email, nom)"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input"
            data-testid="admin-assign-search"
            autoFocus
          />
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {loading && <div className="grid place-items-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
            {!loading && results.length === 0 && (
              <div className="rounded-lg bg-neutral-50 street-border-thin p-3 text-center text-[11px] text-foreground/50">
                Aucun résultat
              </div>
            )}
            {results.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => pick(u)}
                disabled={busy}
                data-testid={`admin-assign-pick-${u.id}`}
                className="w-full text-left rounded-lg bg-white street-border-thin street-press p-2.5 disabled:opacity-50"
              >
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {kind === "driver" && (
                        <span className={`inline-block h-2 w-2 rounded-full ${u.online ? "bg-emerald-500" : "bg-neutral-400"}`} title={u.online ? "En ligne" : "Hors ligne"} />
                      )}
                      <span className="font-black text-[13px] truncate">{u.name || u.email}</span>
                    </div>
                    <div className="text-[11px] text-foreground/60 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                    {u.car && <div className="text-[10px] text-foreground/50 mt-0.5 truncate">🚗 {u.car} {u.plate ? `· ${u.plate}` : ""}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    {typeof u.distance_km === "number" && (
                      <div className="text-[11px] font-black tabular-nums text-primary">
                        📍 {u.distance_km.toFixed(1)} km
                      </div>
                    )}
                    {typeof u.rating === "number" && u.rating > 0 && (
                      <div className="text-[11px] font-black tabular-nums">⭐ {u.rating.toFixed(1)}</div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────── */
/* Tab: Photos — modération des photos de profil                      */
/* ────────────────────────────────────────────────────────────────── */
function PhotosTab() {
  const [filter, setFilter] = useState("pending");
  const { data: items, loading, reload } = useAdminFetch(
    `admin:price:profile-photos:${filter}`,
    async () => {
      const r = await api.get(`/admin/price/profile-photos${filter ? `?status=${filter}` : ""}`);
      return r.data?.items || [];
    },
    { ttlMs: 15_000, initialData: [] },
  );
  const [rejectingUserId, setRejectingUserId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = () => reload(true);

  const approve = async (uid) => {
    try {
      await api.post(`/admin/price/profile-photos/${uid}/approve`);
      toast.success("Photo validée");
      clearAdminCache(`admin:price:profile-photos:${filter}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Erreur"));
    }
  };

  const doReject = async () => {
    if (!rejectingUserId) return;
    try {
      await api.post(`/admin/price/profile-photos/${rejectingUserId}/reject`, {
        reason: rejectReason.trim() || "Photo non conforme",
      });
      toast.success("Photo refusée");
      setRejectingUserId(null);
      setRejectReason("");
      clearAdminCache(`admin:price:profile-photos:${filter}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Erreur"));
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-photos-tab">
      <div className="flex items-center justify-between">
        <div className="font-[Outfit] text-[18px] font-black">Modération photos</div>
        <div className="flex gap-1">
          {["pending", "approved", "rejected"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              data-testid={`admin-photos-filter-${f}`}
              className={
                "street-press h-8 px-3 rounded-full text-[11px] font-[Outfit] font-black uppercase tracking-widest street-border-thin " +
                (filter === f ? "bg-primary text-primary-foreground" : "bg-white")
              }
            >
              {f === "pending" ? "Attente" : f === "approved" ? "OK" : "KO"}
            </button>
          ))}
        </div>
      </div>

      {loading && items.length === 0 && (
        <AdminRowSkeleton count={4} />
      )}
      {!loading && items.length === 0 && (
        <div className="rounded-2xl bg-white p-4 text-center text-[13px] font-medium text-foreground/60 street-border-thin">
          Aucune photo dans ce filtre
        </div>
      )}
      {items.map((p) => (
        <div key={p.user_id} className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm" data-testid={`admin-photo-${p.user_id}`}>
          <div className="flex items-start gap-3">
            <img src={p.data_url} alt="" className="w-20 h-20 rounded-xl object-cover border-2 border-black flex-none" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-[Outfit] font-black text-black truncate">
                {p.user_email}
              </div>
              <div className="text-[11px] font-[Outfit] font-black uppercase tracking-widest text-foreground/60 mt-0.5">
                {p.user_role}
              </div>
              <div className="text-[10px] text-foreground/60 mt-1">
                {p.uploaded_at ? new Date(p.uploaded_at).toLocaleString("fr-FR") : ""}
              </div>
              {p.rejection_reason && (
                <div className="text-[11px] text-foreground/70 mt-1 font-medium">
                  Motif refus : {p.rejection_reason}
                </div>
              )}
            </div>
          </div>
          {p.status === "pending" && (
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => approve(p.user_id)}
                data-testid={`admin-photo-approve-${p.user_id}`}
                className="street-press flex-1 h-9 rounded-xl bg-[#2A9D3F] text-white font-[Outfit] text-[12px] font-black street-border-thin street-shadow-sm flex items-center justify-center gap-1"
              >
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={3} /> Valider
              </button>
              <button
                type="button"
                onClick={() => { setRejectingUserId(p.user_id); setRejectReason(""); }}
                data-testid={`admin-photo-reject-${p.user_id}`}
                className="street-press flex-1 h-9 rounded-xl bg-[#FF3B3B] text-white font-[Outfit] text-[12px] font-black street-border-thin street-shadow-sm flex items-center justify-center gap-1"
              >
                <XCircle className="h-3.5 w-3.5" strokeWidth={3} /> Refuser
              </button>
            </div>
          )}
        </div>
      ))}

      {/* Reject reason modal */}
      {rejectingUserId && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm grid place-items-center px-4" onClick={() => setRejectingUserId(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-4 border-2 border-black shadow-[0_8px_0_0_#000]" onClick={(e) => e.stopPropagation()}>
            <div className="font-[Outfit] text-[15px] font-black mb-2">Motif du refus</div>
            <input
              autoFocus
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex. Selfie flou / visage non visible"
              maxLength={200}
              data-testid="admin-photo-reject-reason-input"
              className="w-full h-11 rounded-xl border-2 border-black px-3 font-[Outfit] text-[13px] font-medium bg-[#FFF8F1]"
              style={{ fontSize: "16px" }}
            />
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => setRejectingUserId(null)}
                className="flex-1 h-11 rounded-xl bg-white text-black font-[Outfit] font-black text-[13px] border-2 border-black"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={doReject}
                data-testid="admin-photo-reject-confirm"
                className="flex-1 h-11 rounded-xl bg-[#FF3B3B] text-white font-[Outfit] font-black text-[13px] border-2 border-black shadow-[0_2px_0_0_#000]"
              >
                Confirmer refus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



/* ------------------------------------------------------------------ */
/* Tab: Popups admin (in-app blocking modals)                          */
/* ------------------------------------------------------------------ */

const POPUP_ACTIONS = [
  { v: "none", label: "Aucune" },
  { v: "custom_link", label: "🔗 Ouvrir un lien externe" },
  { v: "stripe_connect", label: "Ouvrir Stripe Connect" },
  { v: "profile_photo", label: "Ouvrir photo de profil" },
  { v: "wallet", label: "Ouvrir portefeuille" },
  { v: "history", label: "Ouvrir historique" },
  { v: "profile", label: "Ouvrir mon compte" },
  { v: "store_update", label: "🔄 Mettre à jour l'app (App Store / Play Store)" },
];

function PopupTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerPreset, setComposerPreset] = useState(null);
  const [sendingCity, setSendingCity] = useState(false);
  const [openResponses, setOpenResponses] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/price/popups");
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette popup pour tous les destinataires ?")) return;
    try {
      await api.delete(`/admin/price/popups/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Popup supprimée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-3" data-testid="popup-tab">
      <button
        type="button"
        onClick={() => {
          // Ouvre le composer pré-rempli pour que l'admin puisse
          // ajuster le son (Price / Système / Discret) et le texte
          // avant d'envoyer la mise à jour à TOUS les utilisateurs.
          setComposerPreset({
            target_type: "all",
            title: "🔄 Mise à jour disponible",
            body: "Une nouvelle version de Price est disponible sur ton store. Mets à jour pour profiter des dernières améliorations.",
            action_label: "Mettre à jour",
            action_target: "store_update",
            push_sound: "price",
          });
          setComposerOpen(true);
        }}
        data-testid="popup-send-update-btn"
        className="street-press flex w-full items-center gap-3 rounded-2xl bg-emerald-500 p-4 street-border street-shadow text-left"
      >
        <div className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-white street-border-thin">
          <RotateCw className="h-5 w-5 text-emerald-600" strokeWidth={2.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[Outfit] text-[15px] font-black text-white leading-none">
            🔄 Envoyer mise à jour app
          </div>
          <div className="mt-1 text-[11px] font-medium text-white/90">
            Pop-up + notif à TOUS · choix du son · bouton "Mettre à jour" ouvre le store
          </div>
        </div>
        <Send className="h-5 w-5 text-white" strokeWidth={2.5} />
      </button>

      <button
        type="button"
        onClick={async () => {
          if (sendingCity) return;
          if (!window.confirm("Envoyer un popup 'Dis-nous ta ville' à TOUS les utilisateurs sans ville ?")) return;
          setSendingCity(true);
          try {
            const { data } = await api.post("/admin/price/popups/ask-city");
            toast.success(data?.sent_to > 0
              ? `✅ Popup envoyé à ${data.sent_to} utilisateurs`
              : (data?.message || "Tous les utilisateurs ont déjà une ville"));
            load();
          } catch (err) {
            toast.error(formatApiError(err));
          } finally {
            setSendingCity(false);
          }
        }}
        disabled={sendingCity}
        data-testid="popup-ask-city-btn"
        className="street-press flex w-full items-center gap-3 rounded-2xl bg-sky-500 p-4 street-border street-shadow text-left disabled:opacity-60"
      >
        <div className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-white street-border-thin">
          <MapPin className="h-5 w-5 text-sky-600" strokeWidth={2.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[Outfit] text-[15px] font-black text-white leading-none">
            {sendingCity ? "Envoi en cours…" : "📍 Demander la ville à tous"}
          </div>
          <div className="mt-1 text-[11px] font-medium text-white/90">
            Push + popup aux users sans ville · CityPicker intégré · se met direct sur la carte
          </div>
        </div>
        <Send className="h-5 w-5 text-white" strokeWidth={2.5} />
      </button>

      <button
        type="button"
        onClick={() => { setComposerPreset(null); setComposerOpen(true); }}
        data-testid="popup-new-btn"
        className="street-press flex w-full items-center gap-3 rounded-2xl bg-primary p-4 street-border street-shadow text-left"
      >
        <div className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-white street-border-thin">
          <AlertCircle className="h-5 w-5 text-primary" strokeWidth={2.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[Outfit] text-[15px] font-black text-primary-foreground leading-none">Nouveau message popup</div>
          <div className="mt-1 text-[11px] font-medium text-primary-foreground/85">
            S&apos;affiche en modale bloquante dans l&apos;app à la prochaine ouverture
          </div>
        </div>
        <Send className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
      </button>

      <div className="rounded-2xl bg-white p-3 street-border-thin">
        <div className="text-[11px] font-black uppercase tracking-widest text-foreground/70 mb-2">Popups envoyés</div>
        {loading && (
          <div className="py-6 text-center text-sm text-foreground/60">Chargement…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="py-6 text-center text-sm text-foreground/60">Aucune popup pour l&apos;instant</div>
        )}
        {!loading && items.length > 0 && (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} className="rounded-xl bg-secondary/40 p-3 street-border-thin" data-testid={`popup-item-${it.id}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-[Outfit] text-[13px] font-black leading-tight truncate">{it.title}</div>
                    <div className="mt-0.5 text-[11px] text-foreground/70 line-clamp-2">{it.body}</div>
                    <div className="mt-1 flex flex-wrap gap-1 text-[10px] font-black uppercase tracking-widest">
                      <span className="rounded-full bg-white px-2 py-0.5 street-border-thin">{it.target_type}</span>
                      {it.action_target && it.action_target !== "none" && (
                        <span className="rounded-full bg-amber-200 px-2 py-0.5 street-border-thin">
                          {it.action_label}
                        </span>
                      )}
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 street-border-thin">
                        {it.stats?.acted || 0} / {it.stats?.total || 0} agi
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-none">
                    <button
                      type="button"
                      onClick={() => setOpenResponses(it)}
                      data-testid={`popup-responses-${it.id}`}
                      className="rounded-lg bg-blue-600 text-white street-border-thin street-press px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest"
                      aria-label="Voir les réponses"
                    >
                      Réponses
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(it.id)}
                      data-testid={`popup-delete-${it.id}`}
                      className="grid h-8 w-full place-items-center rounded-lg bg-red-500 text-white street-border-thin street-press"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AdminPopupComposer
        open={composerOpen}
        onClose={() => { setComposerOpen(false); setComposerPreset(null); }}
        preset={composerPreset}
        onSent={load}
      />

      {openResponses && (
        <PopupResponsesDialog
          popup={openResponses}
          onClose={() => setOpenResponses(null)}
        />
      )}
    </div>
  );
}

/**
 * AdminPopupComposer — dialog to create a popup targeting a user or
 * a group. Can be opened from the Popup tab (no preset) or from a
 * user row (preset={target_type:"user", target_user_id, targetLabel}).
 */
function AdminPopupComposer({ open, onClose, preset, onSent }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [targetType, setTargetType] = useState(preset?.target_type || "all");
  const [targetUser, setTargetUser] = useState(null);
  const [actionTarget, setActionTarget] = useState("none");
  const [actionLabel, setActionLabel] = useState("");
  const [alsoPush, setAlsoPush] = useState(true);
  const [pushSound, setPushSound] = useState("default"); // default | discreet | price
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [cropSrc, setCropSrc] = useState(null); // dataURL affiché dans le dialog de crop
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      setTitle(preset?.title || "");
      setBody(preset?.body || "");
      setTargetType(preset?.target_type || "all");
      setTargetUser(null);
      setActionTarget(preset?.action_target || "none");
      setActionLabel(preset?.action_label || "");
      setAlsoPush(true);
      setPushSound(preset?.push_sound || "default");
      setImageUrl("");
      setLinkUrl("");
      setUploading(false);
      setCropSrc(null);
    }
  }, [open, preset]);

  if (!open) return null;

  // Étape 1 : l'admin choisit un fichier → on ouvre le dialog de crop
  // AVANT d'uploader, pour qu'il cadre exactement ce que les users
  // verront (l'image s'affiche en `object-cover` dans le popup → sans
  // recadrage, une partie de l'image serait tronquée).
  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Seules les images (PNG/JPG/WEBP) sont acceptées");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image source trop lourde (12 Mo max)");
      return;
    }
    // Lit le fichier en dataURL et l'affiche dans ImageCropDialog.
    const reader = new FileReader();
    reader.onload = () => setCropSrc(String(reader.result || ""));
    reader.onerror = () => toast.error("Lecture de l'image impossible");
    reader.readAsDataURL(file);
    // reset input value so re-selecting the same file works
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Étape 2 : l'admin valide le recadrage → on uploade le blob JPEG
  // recadré (≤ 1600px de large, qualité 88) sur le stockage.
  const uploadCroppedBlob = async (blob, filename) => {
    setCropSrc(null);
    if (!blob) return;
    if (blob.size > 8 * 1024 * 1024) {
      toast.error("Image trop lourde après recadrage (8 Mo max)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", blob, filename || "popup.jpg");
      // Ne PAS forcer Content-Type — axios/browser doit ajouter le
      // boundary automatiquement ("Missing boundary in multipart" sinon).
      const r = await api.post("/admin/upload-image", form);
      setImageUrl(r?.data?.url || "");
      toast.success("Image uploadée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const send = async () => {
    const t = title.trim();
    const b = body.trim();
    if (!t) return toast.error("Titre obligatoire");
    if (!b) return toast.error("Message obligatoire");
    if (targetType === "user" && !preset?.target_user_id && !targetUser) {
      return toast.error("Sélectionne un utilisateur");
    }
    if (actionTarget !== "none" && !actionLabel.trim()) {
      return toast.error("Libellé du bouton d'action obligatoire");
    }
    if (actionTarget === "custom_link") {
      const u = linkUrl.trim();
      if (!u) return toast.error("URL du lien obligatoire");
      if (!(u.startsWith("http://") || u.startsWith("https://"))) {
        return toast.error("URL doit commencer par http:// ou https://");
      }
    }
    setSending(true);
    try {
      const payload = {
        title: t,
        body: b,
        target_type: targetType,
        target_user_id: targetType === "user" ? (preset?.target_user_id || targetUser?.id) : undefined,
        action_target: actionTarget,
        action_label: actionTarget !== "none" ? actionLabel.trim() : undefined,
        image_url: imageUrl.trim() || undefined,
        link_url: actionTarget === "custom_link" ? linkUrl.trim() : undefined,
        also_push: alsoPush,
        push_sound: pushSound,
      };
      const r = await api.post("/admin/price/popups", payload);
      toast.success(
        alsoPush
          ? `📢 Popup + notif push envoyée à ${r.data?.sent_to || 0} utilisateur${(r.data?.sent_to || 0) > 1 ? "s" : ""}`
          : `Popup silencieuse envoyée à ${r.data?.sent_to || 0} utilisateur${(r.data?.sent_to || 0) > 1 ? "s" : ""}`
      );
      onSent && onSent();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSending(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-end sm:items-center sm:justify-center bg-black/60 backdrop-blur-sm px-3"
      onClick={onClose}
      data-testid="popup-composer"
    >
      <div
        className="w-full sm:max-w-md bg-[#FFF8F1] rounded-t-3xl sm:rounded-3xl border-2 border-black shadow-[0_8px_0_0_#000] flex flex-col overflow-hidden max-h-[92dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-black bg-white">
          <div className="font-[Outfit] text-[15px] font-black">Nouveau message popup</div>
          <button
            type="button"
            onClick={onClose}
            data-testid="popup-composer-close"
            aria-label="Fermer"
            className="w-9 h-9 rounded-xl border-2 border-black grid place-items-center bg-white active:translate-y-[2px] transition-transform"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {preset?.targetLabel && (
            <div className="rounded-xl bg-white p-3 street-border-thin">
              <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Destinataire</div>
              <div className="mt-1 font-[Outfit] text-[13px] font-black truncate" data-testid="popup-composer-target-label">
                {preset.targetLabel}
              </div>
            </div>
          )}
          {!preset?.target_user_id && (
            <div className="rounded-xl bg-white p-3 street-border-thin">
              <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 mb-1.5">Cible</div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { v: "all", label: "Tous" },
                  { v: "clients", label: "Pax" },
                  { v: "drivers", label: "Covoit." },
                  { v: "user", label: "1 user" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setTargetType(o.v)}
                    data-testid={`popup-composer-target-${o.v}`}
                    className={`street-press rounded-xl py-2 text-[11px] font-black uppercase tracking-widest street-border-thin ${targetType === o.v ? "bg-primary text-primary-foreground" : "bg-white text-foreground"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {targetType === "user" && (
                <div className="mt-2">
                  <UserPicker value={targetUser} onChange={setTargetUser} label="Utilisateur ciblé" />
                </div>
              )}
            </div>
          )}
          <TemplateBar
            kind="popup"
            current={{ title, body, action_label: actionLabel, action_target: actionTarget }}
            onLoad={(t) => {
              setTitle(t.title || "");
              setBody(t.body || "");
              setActionLabel(t.action_label || "");
              setActionTarget(t.action_target || "none");
            }}
          />
          <div className="rounded-xl bg-white p-3 street-border-thin">
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 mb-1.5">Titre</div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={80}
              data-testid="popup-composer-title"
              placeholder="Ex. Compte Stripe à valider"
              className="w-full rounded-lg bg-secondary/40 px-3 py-2 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:bg-white"
              style={{ fontSize: "16px" }}
            />
            <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-foreground/60 mb-1.5">Message</div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              maxLength={500}
              data-testid="popup-composer-body"
              placeholder="Explique clairement l'action attendue…"
              className="w-full rounded-lg bg-secondary/40 px-3 py-2 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:bg-white resize-none"
              style={{ fontSize: "16px" }}
            />
          </div>
          <div className="rounded-xl bg-white p-3 street-border-thin">
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 mb-1.5">Bouton d&apos;action (optionnel)</div>
            <select
              value={actionTarget}
              onChange={(e) => setActionTarget(e.target.value)}
              data-testid="popup-composer-action-target"
              className="w-full rounded-lg bg-secondary/40 px-3 py-2 font-[Outfit] font-medium text-foreground street-border-thin outline-none focus:bg-white"
              style={{ fontSize: "16px" }}
            >
              {POPUP_ACTIONS.map((a) => (
                <option key={a.v} value={a.v}>{a.label}</option>
              ))}
            </select>
            {actionTarget !== "none" && (
              <input
                value={actionLabel}
                onChange={(e) => setActionLabel(e.target.value)}
                maxLength={40}
                data-testid="popup-composer-action-label"
                placeholder="Libellé du bouton (ex. Configurer maintenant)"
                className="mt-2 w-full rounded-lg bg-secondary/40 px-3 py-2 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:bg-white"
                style={{ fontSize: "16px" }}
              />
            )}
            {actionTarget === "custom_link" && (
              <input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                type="url"
                maxLength={1000}
                inputMode="url"
                data-testid="popup-composer-link-url"
                placeholder="URL à ouvrir (https://…)"
                className="mt-2 w-full rounded-lg bg-secondary/40 px-3 py-2 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:bg-white"
                style={{ fontSize: "16px" }}
              />
            )}
          </div>

          {/* Image optionnelle */}
          <div className="rounded-xl bg-white p-3 street-border-thin">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Image (optionnelle)</div>
              {imageUrl && (
                <button
                  type="button"
                  onClick={() => setImageUrl("")}
                  data-testid="popup-composer-image-remove"
                  className="text-[10px] font-black uppercase text-rose-600 underline"
                >
                  Retirer
                </button>
              )}
            </div>
            {imageUrl ? (
              <div className="rounded-lg overflow-hidden border-2 border-black">
                <img
                  src={imageUrl}
                  alt="Aperçu"
                  className="w-full max-h-[180px] object-cover"
                  data-testid="popup-composer-image-preview"
                />
              </div>
            ) : (
              <label
                htmlFor="popup-image-file"
                className={
                  "flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-black bg-secondary/30 py-3 px-3 cursor-pointer active:translate-y-[1px] transition-transform " +
                  (uploading ? "opacity-60 pointer-events-none" : "")
                }
                data-testid="popup-composer-image-pick"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="font-[Outfit] text-[13px] font-black">Upload en cours…</span>
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4" strokeWidth={2.5} />
                    <span className="font-[Outfit] text-[13px] font-black">Choisir une image</span>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  id="popup-image-file"
                  type="file"
                  accept="image/*"
                  onChange={onPickImage}
                  className="hidden"
                  data-testid="popup-composer-image-input"
                />
              </label>
            )}
            <div className="mt-1.5 text-[10px] text-foreground/50 font-medium">
              8 Mo max. S&apos;affiche au centre du popup + jointe à la notif push.
            </div>
          </div>
          {/* Toggle push notif device */}
          <label className="flex items-start gap-3 rounded-xl bg-white p-3 street-border-thin cursor-pointer">
            <input
              type="checkbox"
              checked={alsoPush}
              onChange={(e) => setAlsoPush(e.target.checked)}
              data-testid="popup-composer-also-push"
              className="mt-0.5 h-5 w-5 accent-primary"
            />
            <div className="flex-1">
              <div className="text-[13px] font-[Outfit] font-black">📢 Envoyer aussi comme notification push</div>
              <div className="text-[11px] text-foreground/60 font-medium mt-0.5">
                Le destinataire recevra une notif sur son téléphone même si l&apos;app est fermée. Décoche pour un popup silencieux visible seulement à la prochaine ouverture.
              </div>
            </div>
          </label>

          {/* Choix du son — seulement si notif push activée */}
          {alsoPush && (
            <div className="rounded-xl bg-white p-3 street-border-thin">
              <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 mb-1.5">
                🔊 Son de la notification
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: "discreet", label: "Discret", desc: "Silencieux" },
                  { v: "default", label: "Système", desc: "Son natif" },
                  { v: "price", label: "Price", desc: "Chime Price" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setPushSound(o.v)}
                    data-testid={`popup-composer-sound-${o.v}`}
                    className={`street-press rounded-xl py-2 px-1 text-center street-border-thin ${pushSound === o.v ? "bg-primary text-primary-foreground" : "bg-white text-foreground"}`}
                  >
                    <div className="text-[12px] font-[Outfit] font-black uppercase tracking-wider leading-tight">{o.label}</div>
                    <div className={`text-[10px] mt-0.5 ${pushSound === o.v ? "text-primary-foreground/80" : "text-foreground/55"} font-medium`}>{o.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="border-t-2 border-black bg-white px-3 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-xl bg-white text-black font-[Outfit] font-black text-[13px] uppercase tracking-wider border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending}
            data-testid="popup-composer-send"
            className="flex-[1.4] h-11 rounded-xl bg-primary text-primary-foreground font-[Outfit] font-black text-[13px] uppercase tracking-wider border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            Envoyer
          </button>
        </div>
      </div>

      {/* Dialog de recadrage — s'ouvre APRÈS que l'admin choisit un
          fichier, AVANT l'upload sur le stockage. */}
      <ImageCropDialog
        open={!!cropSrc}
        fileUrl={cropSrc || ""}
        onCancel={() => setCropSrc(null)}
        onConfirm={(blob, name) => uploadCroppedBlob(blob, name)}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Referrals Tab — parrainages, cagnottes, commissions
// ---------------------------------------------------------------------------
function ReferralsTab() {
  const [overview, setOverview] = useState(null);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [sort, setSort] = useState("commission");
  const [loading, setLoading] = useState(true);
  const [openUserId, setOpenUserId] = useState(null);

  const loadOverview = async () => {
    try {
      const { data } = await api.get("/admin/price/referrals/overview");
      setOverview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (role !== "all") params.set("role", role);
      params.set("sort", sort);
      params.set("limit", "200");
      const { data } = await api.get(`/admin/price/referrals/users?${params}`);
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOverview(); }, []);
  useEffect(() => {
    const t = setTimeout(loadUsers, 200);
    return () => clearTimeout(t);
  }, [q, role, sort]);

  const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

  return (
    <div className="space-y-4" data-testid="admin-referrals-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-2">
        <RefKpiCard label="Codes actifs" value={overview?.codes_active_total ?? "…"} sub={`${overview?.codes_active_clients ?? 0} clients · ${overview?.codes_active_drivers ?? 0} covoit.`} />
        <RefKpiCard label="Filleuls" value={overview?.filleuls_inscrits ?? "…"} sub={`${overview?.filleuls_convertis ?? 0} convertis`} />
        <RefKpiCard label="€ parrainage" value={fmtEur(overview?.total_referral_eur)} sub={`Bonus ${fmtEur(overview?.bonus_referral_eur)} / conversion`} />
        <RefKpiCard label="Commission Price" value={fmtEur(overview?.total_commission_eur)} sub="Total plateforme (rides live)" highlight />
      </div>

      {/* Filtres */}
      <div className="rounded-2xl bg-white p-3 street-border street-shadow-sm">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-foreground/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un nom, email, code…"
            data-testid="admin-ref-search"
            className="flex-1 rounded-lg bg-secondary/40 px-3 py-2 font-[Outfit] text-[13px] font-medium outline-none focus:bg-white street-border-thin"
            style={{ fontSize: "16px" }}
          />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {[
            { v: "all", l: "Tous" },
            { v: "client", l: "Passagers" },
            { v: "driver", l: "Covoit." },
          ].map((r) => (
            <button
              key={r.v}
              type="button"
              onClick={() => setRole(r.v)}
              data-testid={`admin-ref-role-${r.v}`}
              className={`h-7 rounded-full px-2.5 text-[11px] font-black uppercase tracking-widest border-2 border-black ${role === r.v ? "bg-primary text-primary-foreground" : "bg-white"}`}
            >
              {r.l}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-foreground/50">Tri</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="admin-ref-sort"
              className="h-7 rounded-lg border-2 border-black bg-white px-1.5 text-[11px] font-bold outline-none"
            >
              <option value="commission">Commission ↓</option>
              <option value="earned">€ gagnés ↓</option>
              <option value="filleuls">Filleuls ↓</option>
              <option value="recent">Récents</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="rounded-2xl bg-white p-2 street-border street-shadow-sm">
        <div className="px-2 py-1.5 text-[10px] font-black uppercase tracking-widest text-foreground/50">
          {loading ? "Chargement…" : `${items.length} / ${count} utilisateurs`}
        </div>
        <div className="space-y-1.5">
          {items.map((u) => (
            <button
              key={`${u.role}-${u.user_id}`}
              type="button"
              onClick={() => setOpenUserId(u.user_id)}
              data-testid={`admin-ref-user-${u.user_id}`}
              className="w-full text-left rounded-xl bg-secondary/40 hover:bg-secondary p-2.5 street-border-thin transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className={`grid h-8 w-8 flex-none place-items-center rounded-lg border-2 border-black text-[10px] font-black uppercase ${u.role === "driver" ? "bg-[#FFE9D6]" : "bg-[#FFD9DA]"}`}>
                  {u.role === "driver" ? "COV" : "PAX"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-[Outfit] text-[13px] font-black truncate">{u.name}</div>
                  <div className="text-[10px] font-medium text-foreground/60 truncate">{u.email}</div>
                </div>
                <div className="text-right flex-none">
                  <div className="font-[Outfit] text-[13px] font-black text-emerald-700">{fmtEur(u.commission_total_eur)}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50">Commission</div>
                </div>
              </div>
              <div className="mt-1.5 flex items-center flex-wrap gap-1.5 text-[10px] font-bold">
                <span className="rounded-full bg-white px-2 py-0.5 border border-black/20">Code · <span className="font-black tracking-widest">{u.code || "—"}</span></span>
                <span className="rounded-full bg-white px-2 py-0.5 border border-black/20">Filleuls · <b>{u.filleuls_total}</b></span>
                <span className="rounded-full bg-white px-2 py-0.5 border border-black/20">Convertis · <b>{u.filleuls_convertis}</b></span>
                <span className="rounded-full bg-white px-2 py-0.5 border border-black/20">Gagné · <b>{fmtEur(u.total_earned_eur)}</b></span>
              </div>
            </button>
          ))}
          {!loading && items.length === 0 && (
            <div className="py-8 text-center text-[12px] font-bold text-foreground/50">Aucun utilisateur</div>
          )}
        </div>
      </div>

      {openUserId && (
        <ReferralUserDetail
          userId={openUserId}
          onClose={() => setOpenUserId(null)}
          onChanged={() => { loadOverview(); loadUsers(); }}
        />
      )}
    </div>
  );
}

function RefKpiCard({ label, value, sub, highlight }) {
  return (
    <div
      className={`rounded-2xl p-3 street-border street-shadow-sm ${highlight ? "bg-primary text-primary-foreground" : "bg-white"}`}
      data-testid={`admin-ref-kpi-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className={`text-[10px] font-black uppercase tracking-widest ${highlight ? "text-primary-foreground/80" : "text-foreground/50"}`}>{label}</div>
      <div className="mt-1 font-[Outfit] text-[20px] font-black leading-none">{value}</div>
      {sub && <div className={`mt-1 text-[10px] font-medium ${highlight ? "text-primary-foreground/80" : "text-foreground/60"}`}>{sub}</div>}
    </div>
  );
}

function ReferralUserDetail({ userId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editCodeOpen, setEditCodeOpen] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/price/referrals/users/${userId}`);
      setData(data);
      setNewCode(data.code || "");
    } catch (e) {
      toast.error(formatApiError(e));
      onClose?.();
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }); }
    catch { return s.slice(0, 10); }
  };

  const saveCode = async () => {
    setBusy(true);
    try {
      const body = newCode?.trim() ? { code: newCode.trim().toUpperCase() } : { code: null };
      const { data: res } = await api.post(`/admin/price/referrals/users/${userId}/set-code`, body);
      toast.success(`Code défini : ${res.code}`);
      setEditCodeOpen(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addCredit = async () => {
    const amt = parseFloat(String(creditAmount).replace(",", "."));
    if (!(amt > 0)) return toast.error("Montant invalide");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/price/referrals/users/${userId}/credit`, {
        amount_eur: amt,
        note: creditNote || null,
      });
      toast.success(`+${fmtEur(amt)} crédités · Nouveau solde ${fmtEur(res.new_balance_eur)}`);
      setCreditAmount("");
      setCreditNote("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data?.code || "");
      toast.success("Code copié");
    } catch {/*noop*/}
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center sm:justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="admin-ref-detail"
    >
      <div
        className="w-full sm:max-w-lg bg-[#FFF8F1] rounded-t-3xl sm:rounded-3xl border-2 border-black shadow-[0_8px_0_0_#000] flex flex-col overflow-hidden"
        style={{ maxHeight: "92dvh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black bg-white">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50">Parrainage · Détail</div>
            <div className="font-[Outfit] text-[15px] font-black leading-tight truncate">{data?.profile?.client?.full_name || `${data?.profile?.driver?.first_name || ""} ${data?.profile?.driver?.last_name || ""}`.trim() || data?.email || userId}</div>
            <div className="text-[10px] text-foreground/50 truncate">{data?.email}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            data-testid="admin-ref-detail-close"
            className="w-9 h-9 rounded-xl border-2 border-black grid place-items-center bg-white active:translate-y-[2px]"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {loading || !data ? (
            <div className="py-12 text-center text-[12px] font-bold text-foreground/50">Chargement…</div>
          ) : (
            <>
              {/* Code + actions */}
              <div className="rounded-2xl bg-white p-3 street-border street-shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50">Code parrainage</div>
                  <div className="ml-auto flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={copyCode}
                      className="h-7 rounded-full bg-white px-2 border-2 border-black text-[10px] font-black uppercase tracking-widest active:translate-y-[1px] flex items-center gap-1"
                      data-testid="admin-ref-copy-code"
                    >
                      <Copy className="h-3 w-3" /> Copier
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditCodeOpen((v) => !v)}
                      className="h-7 rounded-full bg-primary text-primary-foreground px-2 border-2 border-black text-[10px] font-black uppercase tracking-widest active:translate-y-[1px] flex items-center gap-1"
                      data-testid="admin-ref-edit-code"
                    >
                      <PenSquare className="h-3 w-3" /> Modifier
                    </button>
                  </div>
                </div>
                <div className="mt-2 font-[Outfit] font-black text-[22px] tracking-widest">{data.code || "—"}</div>

                {editCodeOpen && (
                  <div className="mt-3 pt-3 border-t border-black/10">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-foreground/50">
                      Nouveau code (laisser vide pour régénérer)
                    </label>
                    <div className="mt-1 flex gap-2">
                      <input
                        value={newCode}
                        onChange={(e) => setNewCode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 12))}
                        placeholder="EX. VIP2026"
                        data-testid="admin-ref-new-code-input"
                        className="flex-1 min-w-0 rounded-lg bg-white border-2 border-black px-3 py-2 font-[Outfit] font-black tracking-widest outline-none focus:ring-4 focus:ring-primary/25"
                        style={{ fontSize: "16px" }}
                      />
                      <button
                        type="button"
                        onClick={saveCode}
                        disabled={busy}
                        data-testid="admin-ref-save-code"
                        className="rounded-lg bg-primary text-primary-foreground border-2 border-black px-3 py-2 text-[11px] font-black uppercase disabled:opacity-50 active:translate-y-[1px]"
                      >
                        {busy ? "…" : "Enregistrer"}
                      </button>
                    </div>
                    <div className="mt-1 text-[10px] font-medium text-foreground/50">3-12 caractères A-Z / 0-9. Doit être unique.</div>
                  </div>
                )}
              </div>

              {/* KPI utilisateur */}
              <div className="grid grid-cols-2 gap-2">
                <MiniStat label="Filleuls inscrits" value={data.filleuls_total} />
                <MiniStat label="Convertis (1er trajet)" value={data.filleuls_convertis} />
                <MiniStat label="Total gagné (parrainage)" value={fmtEur(data.total_earned_referral_eur)} />
                <MiniStat label="Gagné (fidélité)" value={fmtEur(data.total_earned_loyalty_eur)} />
                <MiniStat label="Solde cagnotte" value={fmtEur(data.wallet?.balance_eur)} />
                <MiniStat label="Débité (utilisé)" value={fmtEur(Math.abs(data.total_debited_eur || 0))} />
              </div>

              {/* Commission Price */}
              <div className="rounded-2xl bg-primary/10 p-3 street-border street-shadow-sm">
                <div className="text-[10px] font-black uppercase tracking-widest text-primary/80">Commission Price générée</div>
                <div className="mt-1 font-[Outfit] font-black text-[24px] text-primary leading-none">
                  {fmtEur(data.commission?.total_eur)}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                  <div className="rounded-lg bg-white px-2 py-1.5 border border-black/20">
                    <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50">Comme covoit.</div>
                    <div className="font-bold">{fmtEur(data.commission?.as_driver_eur)} · <span className="text-foreground/50">{data.commission?.rides_as_driver || 0} course{(data.commission?.rides_as_driver || 0) > 1 ? "s" : ""}</span></div>
                  </div>
                  <div className="rounded-lg bg-white px-2 py-1.5 border border-black/20">
                    <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50">Comme passager</div>
                    <div className="font-bold">{fmtEur(data.commission?.as_client_eur)} · <span className="text-foreground/50">{data.commission?.rides_as_client || 0} course{(data.commission?.rides_as_client || 0) > 1 ? "s" : ""}</span></div>
                  </div>
                </div>
              </div>

              {/* Crédit manuel */}
              <div className="rounded-2xl bg-white p-3 street-border street-shadow-sm">
                <div className="flex items-center gap-2">
                  <Plus className="h-4 w-4 text-primary" strokeWidth={2.75} />
                  <div className="text-[11px] font-black uppercase tracking-widest">Crédit manuel (geste co.)</div>
                </div>
                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value.replace(/[^0-9,.]/g, ""))}
                    placeholder="5,00"
                    data-testid="admin-ref-credit-amount"
                    className="w-24 rounded-lg bg-secondary/40 border-2 border-black px-3 py-2 font-[Outfit] font-black outline-none focus:bg-white"
                    style={{ fontSize: "16px" }}
                  />
                  <input
                    value={creditNote}
                    onChange={(e) => setCreditNote(e.target.value)}
                    placeholder="Motif (ex. dédommagement)"
                    data-testid="admin-ref-credit-note"
                    className="flex-1 min-w-0 rounded-lg bg-secondary/40 border-2 border-black px-3 py-2 font-medium outline-none focus:bg-white text-[12px]"
                    style={{ fontSize: "16px" }}
                  />
                  <button
                    type="button"
                    onClick={addCredit}
                    disabled={busy}
                    data-testid="admin-ref-credit-submit"
                    className="rounded-lg bg-primary text-primary-foreground border-2 border-black px-3 py-2 text-[11px] font-black uppercase disabled:opacity-50 active:translate-y-[1px]"
                  >
                    {busy ? "…" : "Créditer"}
                  </button>
                </div>
              </div>

              {/* Filleuls */}
              <div className="rounded-2xl bg-white p-3 street-border street-shadow-sm">
                <div className="text-[11px] font-black uppercase tracking-widest">Filleuls ({data.filleuls?.length || 0})</div>
                <div className="mt-2 space-y-1.5">
                  {(data.filleuls || []).slice(0, 30).map((f) => (
                    <div key={f.user_id} className="flex items-center gap-2 rounded-lg bg-secondary/40 px-2 py-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-bold truncate">{f.name}</div>
                        <div className="text-[10px] text-foreground/50 truncate">{f.email} · {fmtDate(f.joined_at)}</div>
                      </div>
                      <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border-2 border-black ${f.converted ? "bg-emerald-500 text-white" : "bg-white text-foreground/60"}`}>
                        {f.converted ? "Converti" : "En attente"}
                      </span>
                    </div>
                  ))}
                  {(data.filleuls?.length || 0) === 0 && (
                    <div className="py-4 text-center text-[11px] text-foreground/50">Aucun filleul pour l&apos;instant</div>
                  )}
                </div>
              </div>

              {/* Ledger */}
              <div className="rounded-2xl bg-white p-3 street-border street-shadow-sm">
                <div className="text-[11px] font-black uppercase tracking-widest">Mouvements cagnotte ({data.ledger?.length || 0})</div>
                <div className="mt-2 space-y-1">
                  {(data.ledger || []).slice(0, 30).map((l) => {
                    const amt = Number(l.amount_eur || 0);
                    const pos = amt >= 0;
                    return (
                      <div key={l.id} className="flex items-center gap-2 rounded-lg bg-secondary/30 px-2 py-1.5">
                        <div className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border-2 border-black ${l.kind === "referral" ? "bg-[#FFD9DA]" : l.kind === "loyalty" ? "bg-[#FFE9D6]" : l.kind === "admin" ? "bg-emerald-200" : "bg-white"}`}>
                          {l.kind}
                        </div>
                        <div className="flex-1 min-w-0 text-[10px] text-foreground/60 truncate">
                          {fmtDate(l.created_at)} · {l.ref_id?.slice(0, 8) || "—"}
                          {l.meta?.note ? ` · ${l.meta.note}` : ""}
                        </div>
                        <div className={`text-[12px] font-black ${pos ? "text-emerald-700" : "text-foreground/70"}`}>
                          {pos ? "+" : ""}{fmtEur(amt)}
                        </div>
                      </div>
                    );
                  })}
                  {(data.ledger?.length || 0) === 0 && (
                    <div className="py-3 text-center text-[11px] text-foreground/50">Aucun mouvement</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-white p-2 street-border-thin">
      <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50">{label}</div>
      <div className="mt-0.5 font-[Outfit] font-black text-[14px]">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SupportChatTab — Discussions admin ↔ utilisateurs (façon WhatsApp)
// ---------------------------------------------------------------------------
function SupportChatTab() {
  const [chats, setChats] = useState([]);
  const [openChat, setOpenChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/price/support/chats");
      setChats(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const startNew = async () => {
    if (!newUser) return toast.error("Sélectionne un utilisateur");
    try {
      const r = await api.post("/admin/price/support/chats", { user_id: newUser.id });
      setShowNew(false);
      setNewUser(null);
      await load();
      setOpenChat(r.data.chat);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  };

  return (
    <div className="space-y-3" data-testid="admin-support-tab">
      <button
        type="button"
        onClick={() => setShowNew((v) => !v)}
        data-testid="admin-support-new-btn"
        className="street-press flex w-full items-center gap-3 rounded-2xl bg-primary p-4 street-border street-shadow text-left"
      >
        <div className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-white street-border-thin">
          <MessageCircle className="h-5 w-5 text-primary" strokeWidth={2.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[Outfit] text-[15px] font-black text-primary-foreground leading-none">Nouvelle conversation</div>
          <div className="mt-1 text-[11px] font-medium text-primary-foreground/85">Choisis un utilisateur et démarre le chat</div>
        </div>
        <Send className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
      </button>
      {showNew && (
        <div className="rounded-2xl bg-white p-3 street-border-thin space-y-2">
          <UserPicker value={newUser} onChange={setNewUser} />
          <button
            type="button"
            onClick={startNew}
            data-testid="admin-support-start-chat"
            className="w-full rounded-xl bg-primary text-primary-foreground border-2 border-black py-2.5 text-[12px] font-black uppercase tracking-widest active:translate-y-[1px]"
          >
            Ouvrir la conversation
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-white p-2 street-border-thin">
        <div className="px-2 py-1.5 text-[10px] font-black uppercase tracking-widest text-foreground/50">
          {loading ? "Chargement…" : `${chats.length} conversation${chats.length > 1 ? "s" : ""}`}
        </div>
        <div className="space-y-1.5">
          {chats.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setOpenChat(c)}
              data-testid={`admin-support-chat-${c.id}`}
              className="w-full text-left flex items-center gap-2 rounded-xl bg-secondary/40 hover:bg-secondary p-2.5 street-border-thin"
            >
              <div className={`grid h-9 w-9 flex-none place-items-center rounded-lg text-[10px] font-black uppercase ${c.user?.role === "driver" ? "bg-[#FFE9D6]" : "bg-[#FFD9DA]"}`}>
                {c.user?.role === "driver" ? "COV" : "PAX"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 font-[Outfit] text-[13px] font-black truncate">{c.user?.label || c.user_id}</div>
                  <div className="text-[10px] text-foreground/50 flex-none">{fmtTime(c.updated_at)}</div>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex-1 min-w-0 text-[11px] text-foreground/60 truncate">
                    {c.last_sender === "admin" ? "Vous : " : c.last_sender === "user" ? "" : ""}
                    {c.last_snippet || <i className="text-foreground/40">Pas encore de message</i>}
                  </div>
                  {c.unread_admin > 0 && (
                    <span className="flex-none inline-flex items-center justify-center min-w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-black px-1.5 border-2 border-black">
                      {c.unread_admin}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
          {!loading && chats.length === 0 && (
            <div className="py-8 text-center text-[12px] text-foreground/50">Aucune conversation pour l&apos;instant</div>
          )}
        </div>
      </div>

      {openChat && (
        <SupportChatWindow
          chatId={openChat.id}
          userLabel={openChat.user?.label || openChat.user_id}
          onClose={() => { setOpenChat(null); load(); }}
        />
      )}
    </div>
  );
}

function SupportChatWindow({ chatId, userLabel, onClose }) {
  return (
    <SupportChat
      title={userLabel}
      loadUrl={`/admin/price/support/chats/${chatId}`}
      sendUrl={`/admin/price/support/chats/${chatId}/messages`}
      typingUrl={`/admin/price/support/chats/${chatId}/typing`}
      mine="admin"
      onClose={onClose}
      testPrefix="support-chat"
      emptyText="Aucun message. Envoie le premier pour lancer la conversation !"
    />
  );
}


// ---------------------------------------------------------------------------
// IdentityTab — Vérifications d'identité en attente (KYC light)
// ---------------------------------------------------------------------------
function IdentityTab() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [openUserId, setOpenUserId] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/price/identity/pending", { params: { status: statusFilter, limit: 200 } });
      setItems(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  const badgeCls = (s) => {
    if (s === "verified") return "bg-emerald-500 text-white";
    if (s === "pending") return "bg-amber-400 text-neutral-900";
    if (s === "rejected") return "bg-rose-500 text-white";
    return "bg-white";
  };

  return (
    <div className="space-y-3" data-testid="admin-identity-tab">
      <button
        type="button"
        onClick={() => setLinkOpen(true)}
        data-testid="admin-identity-create-link"
        className="street-press flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3 font-[Outfit] text-[14px] font-black text-primary-foreground street-border street-shadow-sm"
      >
        <Link2 className="h-4 w-4" /> Créer un lien d&apos;envoi de documents
      </button>
      {linkOpen && <IdentityLinkDialog onClose={() => { setLinkOpen(false); load(); }} />}
      <AccountsFollowupPanel />
      <div className="rounded-2xl bg-white p-3 street-border-thin">
        <div className="flex flex-wrap gap-1.5">
          {[
            { v: "pending", l: "En attente" },
            { v: "verified", l: "Validés" },
            { v: "rejected", l: "Refusés" },
            { v: "all", l: "Tous" },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setStatusFilter(f.v)}
              data-testid={`admin-identity-filter-${f.v}`}
              className={`h-8 rounded-full px-3 text-[11px] font-black uppercase tracking-widest border-2 border-black ${statusFilter === f.v ? "bg-primary text-primary-foreground" : "bg-white"}`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white p-2 street-border-thin">
        <div className="px-2 py-1.5 text-[10px] font-black uppercase tracking-widest text-foreground/50">
          {loading ? "Chargement…" : `${items.length} soumission${items.length > 1 ? "s" : ""}`}
        </div>
        <div className="space-y-1.5">
          {items.map((it) => (
            <button
              key={`${it.user_id}-${it.role}`}
              type="button"
              onClick={() => setOpenUserId({ id: it.user_id, role: it.role })}
              data-testid={`admin-identity-open-${it.user_id}-${it.role}`}
              className="w-full text-left flex items-center gap-2 rounded-xl bg-secondary/40 hover:bg-secondary p-2.5 street-border-thin"
            >
              <div className={`grid h-9 w-9 flex-none place-items-center rounded-lg text-[10px] font-black uppercase ${it.role === "driver" ? "bg-[#FFE9D6]" : "bg-[#FFD9DA]"}`}>
                {it.role === "driver" ? "COV" : "PAX"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-[Outfit] text-[13px] font-black truncate">{it.name || it.email}</div>
                <div className="text-[10px] text-foreground/60 truncate">{it.email} · {Object.keys(it.documents_meta || {}).length} doc(s)</div>
                <div className="text-[10px] text-foreground/50 mt-0.5">Soumis {fmtDate(it.submitted_at)}</div>
              </div>
              <span className={`flex-none inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border-2 border-black ${badgeCls(it.status)}`}>
                {it.status}
              </span>
            </button>
          ))}
          {!loading && items.length === 0 && (
            <div className="py-8 text-center text-[12px] text-foreground/50">Aucune soumission dans cet état</div>
          )}
        </div>
      </div>

      {openUserId && (
        <IdentityReviewModal userId={openUserId.id} role={openUserId.role} onClose={() => setOpenUserId(null)} onChanged={load} />
      )}
    </div>
  );
}

const IDENTITY_KIND_LABELS = {
  id_recto: "Pièce d'identité — recto",
  id_verso: "Pièce d'identité — verso",
  driving_license_recto: "Permis — recto",
  driving_license_verso: "Permis — verso",
  insurance: "Attestation d'assurance",
  carte_grise: "Carte grise",
};

function IdentityReviewModal({ userId, role, onClose, onChanged }) {
  const roleQs = role ? `?role=${role}` : "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/price/identity/${userId}${roleQs}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); onClose?.(); }
    finally { setLoading(false); }
  }, [userId, roleQs, onClose]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/price/identity/${userId}/approve${roleQs}`);
      toast.success("Identité validée ✅");
      onChanged?.(); onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    const reason = window.prompt("Motif du refus (visible par l'utilisateur) ?", "Documents non conformes ou illisibles");
    if (!reason) return;
    setBusy(true);
    try {
      await api.post(`/admin/price/identity/${userId}/reject${roleQs}`, { reason });
      toast.success("Identité refusée — user notifié");
      onChanged?.(); onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const docs = data?.documents || {};
  const docsEntries = Object.entries(docs);

  return (
    <div className="fixed inset-0 z-[95] flex items-end sm:items-center sm:justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="admin-identity-modal">
      <div
        className="w-full sm:max-w-lg bg-[#FFF8F1] rounded-t-3xl sm:rounded-3xl border-2 border-black shadow-[0_8px_0_0_#000] flex flex-col overflow-hidden"
        style={{ maxHeight: "92dvh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b-2 border-black bg-white">
          <button type="button" onClick={onClose} data-testid="admin-identity-close" className="w-9 h-9 grid place-items-center rounded-xl border-2 border-black bg-white active:translate-y-[1px]"><X className="h-4 w-4" strokeWidth={3} /></button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50">Vérification d&apos;identité</div>
            <div className="font-[Outfit] text-[15px] font-black leading-tight truncate">{data?.name || data?.email || userId}</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {loading || !data ? (
            <div className="py-12 text-center text-[12px] text-foreground/50">Chargement…</div>
          ) : (
            <>
              <div className="rounded-2xl bg-white p-3 street-border-thin text-[12px]">
                <div><b>Rôle :</b> {data.role === "driver" ? "Conducteur" : "Passager"}</div>
                <div><b>Email :</b> {data.email}</div>
                <div><b>Statut :</b> {data.status}</div>
                {data.rejection_reason && <div><b>Motif refus :</b> {data.rejection_reason}</div>}
              </div>

              <div className="space-y-2">
                {docsEntries.map(([kind, d]) => {
                  const isImg = (d.mime_type || "").startsWith("image/");
                  const src = `data:${d.mime_type};base64,${d.data}`;
                  return (
                    <div key={kind} className="rounded-2xl bg-white p-3 street-border-thin">
                      <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 mb-1.5">{IDENTITY_KIND_LABELS[kind] || kind}</div>
                      {isImg ? (
                        <button
                          type="button"
                          onClick={() => setPreviewDoc({ src, filename: d.filename })}
                          data-testid={`admin-identity-doc-${kind}`}
                          className="w-full"
                        >
                          <img src={src} alt={kind} className="w-full max-h-64 object-contain rounded-lg border-2 border-black bg-black/5" />
                        </button>
                      ) : (
                        <a
                          href={src}
                          download={d.filename}
                          className="inline-block rounded-lg bg-secondary/40 border-2 border-black px-3 py-2 text-[12px] font-bold"
                        >
                          📄 {d.filename}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="p-3 border-t-2 border-black bg-white flex gap-2">
          <button
            type="button"
            onClick={reject}
            disabled={busy || loading}
            data-testid="admin-identity-reject"
            className="flex-1 rounded-2xl bg-rose-500 text-white border-2 border-black py-3 text-[12px] font-black uppercase tracking-widest disabled:opacity-50 active:translate-y-[1px]"
          >
            Refuser
          </button>
          <button
            type="button"
            onClick={approve}
            disabled={busy || loading}
            data-testid="admin-identity-approve"
            className="flex-1 rounded-2xl bg-emerald-600 text-white border-2 border-black py-3 text-[12px] font-black uppercase tracking-widest disabled:opacity-50 active:translate-y-[1px]"
          >
            Valider
          </button>
        </div>
      </div>

      {previewDoc && (
        <div className="fixed inset-0 z-[100] bg-black/90 grid place-items-center p-4" onClick={() => setPreviewDoc(null)}>
          <img src={previewDoc.src} alt={previewDoc.filename} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}



/* -------------------------------------------------------------------------
 * SurveysTab — Admin broadcast questions/sondages.
 *
 * Fonctionnalités :
 *   • Création : question texte libre OU choix multiples, cible all/driver/client
 *   • Liste : tous les sondages avec count de réponses
 *   • Détail : réponses texte + tally pour les choix
 * ------------------------------------------------------------------------- */
function SurveysTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [question, setQuestion] = useState("");
  const [type, setType] = useState("text");
  const [choices, setChoices] = useState(["", ""]);
  const [targetRole, setTargetRole] = useState("all");
  const [targetUser, setTargetUser] = useState(null);
  const [sound, setSound] = useState("default");
  const [openResp, setOpenResp] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/surveys");
      setList(Array.isArray(data?.surveys) ? data.surveys : []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!question.trim()) return toast.error("Question vide");
    if (type === "choice" && choices.filter((c) => c.trim()).length < 2) {
      return toast.error("Au moins 2 choix non-vides requis");
    }
    if (targetRole === "user" && !targetUser?.id) {
      return toast.error("Choisis un utilisateur destinataire");
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/surveys", {
        question: question.trim(),
        type,
        choices: type === "choice" ? choices.map((c) => c.trim()).filter(Boolean) : null,
        target_role: targetRole,
        target_user_id: targetRole === "user" ? targetUser.id : undefined,
        sound,
      });
      toast.success(`Envoyé à ${data?.pushed_to || 0} utilisateur(s)`);
      setQuestion("");
      setChoices(["", ""]);
      setType("text");
      setTargetRole("all");
      setTargetUser(null);
      setSound("default");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const close = async (id) => {
    if (!window.confirm("Clôturer ce sondage ? Les users ne pourront plus répondre.")) return;
    try {
      await api.delete(`/admin/surveys/${id}`);
      toast.success("Sondage clôturé");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-surveys-tab">
      {/* Form création */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="font-semibold text-slate-900 text-[15px]">Nouvelle question</div>
        <div className="mt-3 space-y-3">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ta question (ex: Qu'est-ce que tu penses de la nouvelle version ?)"
            rows={2}
            maxLength={400}
            className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-[13px] outline-none focus:ring-2 focus:ring-blue-400/30"
            data-testid="admin-survey-question"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">Type :</div>
            <button
              onClick={() => setType("text")}
              data-testid="admin-survey-type-text"
              className={`rounded-lg px-3 py-1.5 text-[11px] font-black border ${type === "text" ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700"}`}
            >
              Réponse libre
            </button>
            <button
              onClick={() => setType("choice")}
              data-testid="admin-survey-type-choice"
              className={`rounded-lg px-3 py-1.5 text-[11px] font-black border ${type === "choice" ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700"}`}
            >
              Choix multiples
            </button>
            <div className="ml-6 text-[11px] font-black uppercase tracking-widest text-slate-500">Cible :</div>
            {["all", "driver", "client", "user"].map((r) => (
              <button
                key={r}
                onClick={() => setTargetRole(r)}
                data-testid={`admin-survey-target-${r}`}
                className={`rounded-lg px-3 py-1.5 text-[11px] font-black border ${targetRole === r ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700"}`}
              >
                {r === "all" ? "Tous" : r === "driver" ? "Covoitureurs" : r === "client" ? "Passagers" : "👤 Un seul user"}
              </button>
            ))}
          </div>
          {targetRole === "user" && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
              <UserPicker value={targetUser} onChange={setTargetUser} label="Utilisateur destinataire" />
              <div className="mt-1 text-[11px] text-slate-500 font-medium">
                Ce user seul recevra la notif + popup et pourra répondre.
              </div>
            </div>
          )}
          {type === "choice" && (
            <div className="space-y-2">
              {choices.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={c}
                    onChange={(e) => setChoices((arr) => arr.map((x, j) => j === i ? e.target.value : x))}
                    placeholder={`Choix ${i + 1}`}
                    maxLength={200}
                    className="flex-1 rounded-lg border border-slate-200 bg-white p-2 text-[13px]"
                    data-testid={`admin-survey-choice-${i}`}
                  />
                  {choices.length > 2 && (
                    <button
                      onClick={() => setChoices((arr) => arr.filter((_, j) => j !== i))}
                      className="rounded-lg px-2 py-2 text-[11px] font-black text-slate-500 hover:text-red-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {choices.length < 10 && (
                <button
                  onClick={() => setChoices((arr) => [...arr, ""])}
                  className="text-[11px] font-black text-blue-600"
                >
                  + Ajouter un choix
                </button>
              )}
            </div>
          )}
          {/* Choix du son de la notification */}
          <div>
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-600 mb-1.5">🔊 Son de la notification</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "discreet", label: "Discret", desc: "Silencieux" },
                { v: "default", label: "Système", desc: "Son natif" },
                { v: "price", label: "Price", desc: "Chime Price" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setSound(o.v)}
                  data-testid={`admin-survey-sound-${o.v}`}
                  className={`rounded-lg py-2 px-1 text-center border-2 ${sound === o.v ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white"}`}
                >
                  <div className="text-[12px] font-black uppercase tracking-wider leading-tight text-slate-900">{o.label}</div>
                  <div className="text-[10px] mt-0.5 text-slate-500 font-medium">{o.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={create}
            disabled={creating}
            data-testid="admin-survey-create"
            className="rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-black text-white disabled:opacity-50"
          >
            {creating ? "Envoi…" : "Envoyer à tous les users ciblés"}
          </button>
        </div>
      </div>

      {/* Liste */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="font-semibold text-slate-900 text-[15px]">Historique des sondages</div>
        {loading ? (
          <div className="py-6 text-center text-slate-400 text-sm">Chargement…</div>
        ) : list.length === 0 ? (
          <div className="py-6 text-center text-slate-400 text-sm">Aucun sondage encore envoyé</div>
        ) : (
          <div className="mt-3 space-y-2">
            {list.map((s) => (
              <div key={s.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`admin-survey-row-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{s.question}</div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
                      <span>{s.type === "text" ? "📝 Texte libre" : `🔘 Choix (${(s.choices || []).length})`}</span>
                      <span>•</span>
                      <span>{s.target_role === "all" ? "Tous" : s.target_role === "driver" ? "Covoitureurs" : s.target_role === "user" ? "👤 1 user" : "Passagers"}</span>
                      <span>•</span>
                      <span>{new Date(s.created_at).toLocaleString("fr-FR")}</span>
                      {s.closed_at && <><span>•</span><span className="text-red-600 font-black">Clôturé</span></>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-none">
                    <button
                      onClick={() => setOpenResp(s)}
                      data-testid={`admin-survey-responses-${s.id}`}
                      className="rounded-lg bg-blue-600 px-3 py-1.5 text-[11px] font-black text-white"
                    >
                      {s.response_count || 0} réponses
                    </button>
                    {!s.closed_at && (
                      <button
                        onClick={() => close(s.id)}
                        className="rounded-lg bg-white border border-slate-200 px-2 py-1.5 text-[11px] font-black text-slate-700 hover:text-red-600"
                      >
                        Clôturer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {openResp && <SurveyResponsesDialog survey={openResp} onClose={() => setOpenResp(null)} />}
    </div>
  );
}

function SurveyResponsesDialog({ survey, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/surveys/${survey.id}/responses`);
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [survey.id]);

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/60 p-4" onClick={onClose} data-testid="admin-survey-responses-dialog">
      <div className="w-full max-w-2xl rounded-xl bg-white p-5 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">Question</div>
            <div className="mt-1 text-[15px] font-semibold text-slate-900">{survey.question}</div>
          </div>
          <button onClick={onClose} className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="py-6 text-center text-slate-400 text-sm">Chargement…</div>
        ) : (
          <div className="mt-4 space-y-3">
            {survey.type === "choice" && data?.tallies && (
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2">Résultats</div>
                {(survey.choices || []).map((c) => {
                  const count = data.tallies[c] || 0;
                  const total = Object.values(data.tallies).reduce((a, b) => a + b, 0) || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={c} className="mb-2">
                      <div className="flex justify-between text-[12px] font-semibold text-slate-700">
                        <span>{c}</span>
                        <span className="tabular-nums">{count} ({pct}%)</span>
                      </div>
                      <div className="mt-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                        <div className="h-full bg-blue-600" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="rounded-lg bg-white border border-slate-200">
              <div className="px-3 py-2 text-[11px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-100">
                Toutes les réponses ({(data?.responses || []).length})
              </div>
              {(data?.responses || []).length === 0 ? (
                <div className="py-6 text-center text-slate-400 text-sm">Pas encore de réponses</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {(data?.responses || []).map((r) => (
                    <li key={r.id} className="px-3 py-2.5">
                      <div className="flex items-center gap-2 text-[11px] text-slate-500">
                        <span className="font-semibold text-slate-700">{r.user_email}</span>
                        <span>• {r.user_role === "driver" ? "Covoitureur" : "Passager"}</span>
                        <span>• {new Date(r.created_at).toLocaleString("fr-FR")}</span>
                      </div>
                      <div className="mt-1 text-[13px] text-slate-900">
                        {r.answer_text || <span className="font-black text-blue-700">{r.answer_choice}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



/* -------------------------------------------------------------------------
 * PopupResponsesDialog — Détail des réponses à un popup.
 *
 * Pour les popups "demande de ville" (action_target="set_city"), affiche
 * aussi le classement des villes renseignées + la ville de chaque user.
 * ------------------------------------------------------------------------- */
function PopupResponsesDialog({ popup, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/price/popups/${popup.id}/responses`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [popup.id]);

  const isAskCity = data?.is_ask_city;
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const tally = Array.isArray(data?.tally_by_city) ? data.tally_by_city : [];
  const stats = data?.stats || {};

  return (
    <div
      className="fixed inset-0 z-[9999] grid place-items-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="popup-responses-dialog"
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl max-h-[90vh] overflow-y-auto border-2 border-black"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Réponses au popup</div>
            <div className="mt-1 font-[Outfit] text-[15px] font-black text-slate-900 truncate">{popup.title}</div>
            {isAskCity && (
              <div className="mt-1 text-[11px] font-black uppercase tracking-widest text-sky-700">
                📍 Demande de ville
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="popup-responses-close"
            className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-slate-100 border-2 border-black"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>

        {loading ? (
          <div className="py-10 grid place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {/* Stats compactes */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-slate-50 p-3 border border-slate-200">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Envoyés</div>
                <div className="mt-0.5 text-[18px] font-black text-slate-900">{stats.total || 0}</div>
              </div>
              <div className="rounded-xl bg-emerald-50 p-3 border border-emerald-200">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Ont agi</div>
                <div className="mt-0.5 text-[18px] font-black text-emerald-900">{stats.acted || 0}</div>
              </div>
              <div className="rounded-xl bg-amber-50 p-3 border border-amber-200">
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Fermés</div>
                <div className="mt-0.5 text-[18px] font-black text-amber-900">{stats.dismissed || 0}</div>
              </div>
            </div>

            {/* Top villes — si ask-city */}
            {isAskCity && tally.length > 0 && (
              <div className="rounded-xl bg-slate-50 p-3 border border-slate-200">
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2">
                  🏙️ Classement des villes ({tally.length})
                </div>
                <div className="space-y-1.5">
                  {tally.slice(0, 20).map((t) => {
                    const total = tally.reduce((a, b) => a + b.count, 0) || 1;
                    const pct = Math.round((t.count / total) * 100);
                    return (
                      <div key={t.city}>
                        <div className="flex items-center justify-between text-[12px] font-semibold text-slate-800">
                          <span className="truncate">{t.city}</span>
                          <span className="tabular-nums text-slate-500">{t.count} ({pct}%)</span>
                        </div>
                        <div className="mt-0.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Liste détaillée par utilisateur */}
            <div className="rounded-xl bg-white border border-slate-200">
              <div className="px-3 py-2 text-[11px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-100">
                Détail par utilisateur ({rows.length})
              </div>
              {rows.length === 0 ? (
                <div className="py-6 text-center text-slate-400 text-sm">Aucun destinataire</div>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-[40vh] overflow-y-auto">
                  {rows.map((r) => {
                    const status = r.acted_at ? "acted" : (r.dismissed_at ? "dismissed" : "pending");
                    const statusInfo = status === "acted"
                      ? { label: "✓ Agi", cls: "bg-emerald-100 text-emerald-800" }
                      : status === "dismissed"
                        ? { label: "✕ Fermé", cls: "bg-amber-100 text-amber-800" }
                        : { label: "— En attente", cls: "bg-slate-100 text-slate-600" };
                    return (
                      <li key={r.user_id} className="px-3 py-2.5" data-testid={`popup-response-row-${r.user_id}`}>
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className={`rounded px-1.5 py-0.5 font-black uppercase tracking-wider ${statusInfo.cls}`}>
                            {statusInfo.label}
                          </span>
                          <span className="font-semibold text-slate-700 truncate">{r.email || r.name || r.user_id}</span>
                          <span className="text-slate-500 flex-none">· {r.role === "driver" ? "Covoitureur" : "Passager"}</span>
                        </div>
                        {isAskCity && r.city && (
                          <div className="mt-1 text-[12px] text-slate-900">
                            📍 <span className="font-black">{r.city}</span>
                            {r.city_updated_at && (
                              <span className="ml-2 text-[11px] text-slate-500">
                                · {new Date(r.city_updated_at).toLocaleString("fr-FR")}
                              </span>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * DriversOnlineWidget — Mini-widget "Covoitureurs en ligne" sur la home.
 *
 * Chargement très rapide via /admin/price/drivers/online-quick (projection
 * minimale, pas de ratings, limite 50). Rafraîchi toutes les 30s en silence.
 * ------------------------------------------------------------------------- */
function DriversOnlineWidget({ onOpenFull }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);

  const load = React.useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const r = await api.get("/admin/price/drivers/online-quick");
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setCount(r.data?.count || 0);
    } catch {
      /* silent — le widget est best-effort */
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      load(true);
    }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const fmtAgo = (iso) => {
    if (!iso) return "—";
    try {
      const diff = (Date.now() - new Date(iso).getTime()) / 1000;
      if (diff < 60) return `${Math.max(1, Math.floor(diff))}s`;
      if (diff < 3600) return `${Math.floor(diff / 60)}min`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
      return `${Math.floor(diff / 86400)}j`;
    } catch { return "—"; }
  };

  const topFive = items.slice(0, 5);

  return (
    <div className="mt-7" data-testid="drivers-online-widget">
      <div className="flex items-center justify-between mb-2.5 px-1">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
          Covoitureurs en ligne
        </p>
        <button
          type="button"
          onClick={onOpenFull}
          data-testid="drivers-online-widget-open-full"
          className="text-[11px] font-black uppercase tracking-widest text-blue-600 active:translate-y-[1px]"
        >
          Voir tout →
        </button>
      </div>
      <div className="rounded-2xl bg-white border-2 border-black shadow-[0_3px_0_0_#000] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black bg-emerald-50">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-[Outfit] text-[13px] font-black uppercase tracking-wider text-emerald-900">
              {loading ? "…" : count} en ligne
            </span>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700/70">
            Live
          </span>
        </div>
        {loading && items.length === 0 ? (
          <div className="py-6 grid place-items-center">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
          </div>
        ) : count === 0 ? (
          <div className="py-6 text-center text-[12px] font-medium text-slate-500">
            Aucun covoitureur connecté pour l&apos;instant.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {topFive.map((d) => {
              const name = d.full_name || `${d.first_name || ""} ${d.last_name || ""}`.trim() || d.email;
              return (
                <li key={d.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`drivers-online-widget-row-${d.id}`}>
                  <div className="grid h-9 w-9 flex-none place-items-center rounded-full bg-emerald-100 overflow-hidden border-2 border-black">
                    {d.photo_url ? (
                      <img src={d.photo_url} alt={name} className="h-full w-full object-cover" />
                    ) : (
                      <Car className="h-4 w-4 text-emerald-700" strokeWidth={2.5} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-[Outfit] text-[13px] font-black text-slate-900 truncate">{name}</div>
                    <div className="text-[11px] font-medium text-slate-500 truncate">
                      {d.vehicle_brand_model || d.email}
                    </div>
                  </div>
                  <div className="flex-none text-right">
                    <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                      {fmtAgo(d.price_online_at)}
                    </div>
                  </div>
                </li>
              );
            })}
            {count > topFive.length && (
              <li className="px-4 py-2 text-center">
                <button
                  type="button"
                  onClick={onOpenFull}
                  className="text-[11px] font-black uppercase tracking-widest text-blue-600"
                >
                  + {count - topFive.length} autre{count - topFive.length > 1 ? "s" : ""}
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------
 * DriversFastTab — Liste paginée ultra-rapide des covoitureurs.
 *
 * Backend : /admin/price/drivers/slim (projection légère, pagination,
 * recherche sur email/nom/téléphone). Pensé pour éviter le long
 * chargement de /admin/drivers (500 rows, ratings aggregate).
 * ------------------------------------------------------------------------- */
function DriversFastTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [docsFilter, setDocsFilter] = useState("");
  const [docsCounts, setDocsCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const qDebounceRef = useRef(null);

  const load = React.useCallback(async (nextPage, nextQ, nextStatus, nextDocs) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(nextPage));
      params.set("limit", String(limit));
      if (nextQ && nextQ.trim()) params.set("q", nextQ.trim());
      if (nextStatus) params.set("status_filter", nextStatus);
      if (nextDocs) params.set("docs_filter", nextDocs);
      const r = await api.get(`/admin/price/drivers/slim?${params.toString()}`);
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setTotal(r.data?.total || 0);
      setHasMore(!!r.data?.has_more);
      setDocsCounts(r.data?.docs_counts || null);
      setPage(nextPage);
    } catch (err) {
      toast.error(formatApiError(err, "Impossible de charger les covoitureurs"));
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { load(1, "", "", ""); }, [load]);

  // Debounce la recherche pour pas spammer l'API
  useEffect(() => {
    if (qDebounceRef.current) clearTimeout(qDebounceRef.current);
    qDebounceRef.current = setTimeout(() => { load(1, q, statusFilter, docsFilter); }, 350);
    return () => { if (qDebounceRef.current) clearTimeout(qDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter, docsFilter]);

  return (
    <div className="space-y-3" data-testid="drivers-fast-tab">
      {/* Filtres */}
      <div className="rounded-2xl bg-white p-3 border-2 border-black shadow-[0_3px_0_0_#000]">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Recherche email, nom, téléphone…"
          data-testid="drivers-fast-search"
          className="w-full rounded-lg bg-secondary/40 px-3 py-2 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 street-border-thin outline-none focus:bg-white"
          style={{ fontSize: "16px" }}
        />
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {[
            { v: "", label: "Tous" },
            { v: "approved", label: "Approuvés" },
            { v: "pending", label: "En attente" },
            { v: "suspended", label: "Suspendus" },
            { v: "rejected", label: "Refusés" },
          ].map((s) => (
            <button
              key={s.v}
              type="button"
              onClick={() => setStatusFilter(s.v)}
              data-testid={`drivers-fast-filter-${s.v || "all"}`}
              className={`flex-none rounded-lg px-3 py-1.5 text-[11px] font-black uppercase tracking-wider border-2 border-black ${statusFilter === s.v ? "bg-primary text-primary-foreground" : "bg-white text-foreground"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <DriverDocsFilters value={docsFilter} onChange={setDocsFilter} counts={docsCounts} q={q} statusFilter={statusFilter} onReminded={() => load(page, q, statusFilter, docsFilter)} />
        <div className="mt-2 text-[11px] font-medium text-foreground/60">
          {loading ? "Chargement…" : `${total} covoitureur${total > 1 ? "s" : ""} total · page ${page}`}
        </div>
      </div>

      {/* Liste */}
      {loading && items.length === 0 ? (
        <div className="py-10 grid place-items-center">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-white p-6 text-center border-2 border-black">
          <Car className="h-8 w-8 text-foreground/30 mx-auto mb-2" strokeWidth={2} />
          <p className="font-[Outfit] text-[14px] font-medium text-foreground/60">
            Aucun résultat
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((d) => (
            <li key={d.id} data-testid={`drivers-fast-row-${d.id}`}>
              <UserRow
                u={d}
                kind="drivers"
                onChanged={() => load(page, q, statusFilter, docsFilter)}
              />
              <DriverDocsStrip d={d} onChanged={() => load(page, q, statusFilter, docsFilter)} />
            </li>
          ))}
        </ul>
      )}

      {/* Pagination */}
      {(page > 1 || hasMore) && !loading && (
        <div className="flex items-center justify-between gap-2 pt-2">
          <button
            type="button"
            onClick={() => load(page - 1, q, statusFilter, docsFilter)}
            disabled={page <= 1}
            data-testid="drivers-fast-prev"
            className="street-press rounded-xl bg-white border-2 border-black px-4 py-2 text-[12px] font-black uppercase tracking-widest disabled:opacity-40"
          >
            ← Précédent
          </button>
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
            Page {page}
          </div>
          <button
            type="button"
            onClick={() => load(page + 1, q, statusFilter, docsFilter)}
            disabled={!hasMore}
            data-testid="drivers-fast-next"
            className="street-press rounded-xl bg-white border-2 border-black px-4 py-2 text-[12px] font-black uppercase tracking-widest disabled:opacity-40"
          >
            Suivant →
          </button>
        </div>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------
 * RoleStatsWidget — Répartition passagers / conducteurs par mode actif.
 *
 * Dual-role (user ayant les 2 profils) est compté dans la catégorie dont il
 * utilise le plus le dashboard (`active_mode`). Un user qui ne se connecte
 * plus en passager mais toujours en conducteur passe donc dans "conducteurs"
 * automatiquement.
 * ------------------------------------------------------------------------- */
function RoleStatsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/price/role-stats");
        if (!cancelled) setData(r.data);
      } catch {
        /* silent — widget best-effort */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="mt-7">
        <div className="rounded-2xl bg-white border-2 border-black p-6 grid place-items-center">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      </div>
    );
  }
  if (!data) return null;

  const totalActive = (data.active_as_client || 0) + (data.active_as_driver || 0);
  const pctClient = totalActive > 0 ? Math.round(((data.active_as_client || 0) / totalActive) * 100) : 0;
  const pctDriver = 100 - pctClient;

  return (
    <div className="mt-7" data-testid="admin-role-stats-widget">
      <div className="flex items-center justify-between mb-2.5 px-1">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
          Mode actif (passager / conducteur)
        </p>
        <p className="text-[11px] font-medium text-slate-400">
          sur {data.window_days || 30} j
        </p>
      </div>
      <div className="rounded-2xl bg-white border-2 border-black shadow-[0_3px_0_0_#000] p-4 space-y-4">
        {/* Barre de répartition */}
        <div>
          <div className="flex items-center justify-between text-[11px] font-black uppercase tracking-widest mb-1.5">
            <span className="text-blue-700">Passagers · {data.active_as_client}</span>
            <span className="text-emerald-700">Conducteurs · {data.active_as_driver}</span>
          </div>
          <div className="h-3 w-full rounded-full overflow-hidden border-2 border-black flex">
            <div className="h-full bg-blue-500" style={{ width: `${pctClient}%` }} />
            <div className="h-full bg-emerald-500" style={{ width: `${pctDriver}%` }} />
          </div>
          <div className="mt-1 flex items-center justify-between text-[10px] font-medium text-slate-500">
            <span>{pctClient}%</span>
            <span>{pctDriver}%</span>
          </div>
        </div>

        {/* Détail */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl bg-blue-50 p-3 border border-blue-200">
            <div className="text-[10px] font-black uppercase tracking-widest text-blue-700">Passagers purs</div>
            <div className="mt-0.5 text-[18px] font-black text-blue-900 tabular-nums">{data.pure_clients}</div>
          </div>
          <div className="rounded-xl bg-emerald-50 p-3 border border-emerald-200">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Conducteurs purs</div>
            <div className="mt-0.5 text-[18px] font-black text-emerald-900 tabular-nums">{data.pure_drivers}</div>
          </div>
          <div className="rounded-xl bg-amber-50 p-3 border border-amber-200">
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Dual-role</div>
            <div className="mt-0.5 text-[18px] font-black text-amber-900 tabular-nums">{data.dual_total}</div>
            {data.dual_total > 0 && (
              <div className="mt-0.5 text-[10px] font-medium text-amber-700/80">
                {data.dual_as_driver}🚗 · {data.dual_as_client}🧍
              </div>
            )}
          </div>
        </div>

        {/* Activité récente */}
        <div className="rounded-xl bg-slate-50 p-3 border border-slate-200">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1.5">
            Activité récente ({data.window_days || 30} derniers jours)
          </div>
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-blue-700">🧍 Actifs passager</span>
              <span className="font-black text-slate-900 tabular-nums">{data.recently_active_client}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-emerald-700">🚗 Actifs conducteur</span>
              <span className="font-black text-slate-900 tabular-nums">{data.recently_active_driver}</span>
            </div>
          </div>
          <p className="mt-2 text-[10px] text-slate-500 font-medium leading-relaxed">
            Un user dual qui n&apos;utilise plus que son dashboard conducteur bascule automatiquement dans "Actifs conducteur".
          </p>
        </div>
      </div>
    </div>
  );
}

